import type { Dict } from './types';

/**
 * RepCheck arayüz sözlüğü.
 *
 * Kural: `en` ve `tr` anahtar kümeleri birebir aynı olmalı (i18n testi bunu
 * doğrular). Motorların ürettiği Türkçe metinler `issues.ts` içinde İngilizce
 * şablonlarla eşlenir; bu sözlük yalnızca arayüz metinlerini taşır.
 */

const en: Dict = {
"preview.title": "Your preliminary check is ready",
"preview.eyebrow": "Private document check",
"preview.findings": "findings",
"preview.categories": "Findings by category",
"preview.unlockTitle": "Open the detailed report",
"preview.unlockBody": "Sign in with a LibEdge account that has an active RepCheck assignment to see explanations, locations and suggestions. Your document stays in this tab.",
"preview.waiting": "Complete sign-in in the new tab, then return here. Your analysis will be waiting.",
"preview.privacy": "The document and its text stay on your device. Only numeric usage and result statistics are recorded.",
"preview.another": "Check another document",
"preview.complete": "Sign-in complete",
"preview.return": "Return to your analysis tab to see the detailed report. You can close this tab.",
"preview.openApp": "Open RepCheck",
"preview.quota": "Your monthly full-report allowance is used. You can still view this count-only preview.",
"preview.batchTitle": "Batch analysis with your LibEdge account",
"preview.batchBody": "Sign in with an active RepCheck assignment to analyse a batch. You can try a single-document preview without signing in.",
"auth.plan.institution": "Institution access",
"productAccess.title":"RepCheck access",
"productAccess.assignment":"An active RepCheck assignment is required on your LibEdge account or through your institution.",
"productAccess.help":"Sign in with your LibEdge account to verify access. If RepCheck is missing from your products, contact your institution administrator or LibEdge.",
"productAccess.unavailable":"We could not verify your RepCheck access. Please try again.",
"productAccess.expired":"Please sign in with LibEdge again to verify your access.",
"productAccess.retry":"Check access again",
"productAccess.checking":"Checking…",
"productAccess.account":"My LibEdge account",
"productAccess.logout":"Sign out",
	// ---------------------------------------------------------------- common
	'common.beta': 'beta',
	'common.language': 'Language',
	'common.close': 'Close',
	'common.weight': 'weight {n}%',
	'common.page': 'p. {n}',
	'common.line': 'line {n}',
	'common.chars': 'characters',
	'common.hide': 'Hide',
	'common.show': 'Show',
	'common.yes': 'yes',
	'common.no': 'no',
	'severity.critical': 'Critical',
	'severity.warning': 'Warning',
	'severity.info': 'Info',
	'severity.ok': 'OK',
	'severity.critical.hint': 'A strongly evidenced finding that directly affects the verdict',
	'severity.warning.hint': 'A reporting problem that should be fixed',
	'severity.info.hint': 'A style or preference note',
	'severity.ok.hint': 'No problem detected',
	'category.statistics': 'Statistical reporting',
	'category.citations': 'Citations and references',
	'category.structure': 'Structural integrity',
	'category.language': 'Language and claim strength',
	'category.provenance': 'Extraction confidence',
	'category.access': 'Transparency and blind review',
	'category.short.statistics': 'Statistics',
	'category.short.citations': 'Citations',
	'category.short.structure': 'Structure',
	'category.short.language': 'Language',
	'category.short.provenance': 'Extraction',
	'category.short.access': 'Transparency',
	'category.notChecked': 'not checked',
	'category.findings': '{n} findings',
	'category.criticals': '{n} critical',
	'category.noData': 'no data',
	'confidence.high': 'High confidence',
	'confidence.medium': 'Medium confidence',
	'confidence.low': 'Low confidence',
	'confidence.high.short': 'high',
	'confidence.medium.short': 'medium',
	'confidence.low.short': 'low',
	'design.experimental': 'Experimental',
	'design.quasi': 'Quasi-experimental',
	'design.correlational': 'Correlational / cross-sectional',
	'design.case': 'Case study',
	'design.unknown': 'Not determined',
	'time.justNow': 'just now',
	'time.minutesAgo': '{n} min ago',
	'time.hoursAgo': '{n} h ago',
	'reason.decimalComma': 'Decimal comma used; Turkish number format.',
	'reason.pMissing': 'No p-value was reported in the same expression.',
	'reason.dfMissingT': 'No degrees of freedom for t; p cannot be recomputed.',
	'reason.dfMissingF': 'Degrees of freedom are missing for F.',
	'reason.dfMissingChi': 'No degrees of freedom for χ².',
	'reason.nMissing': 'No sample size for r.',
	'reason.negativeT': 'Negative t value; the direction of the comparison is unclear.',
	'reason.impossible': 'The value lies outside the possible range.',
	'reason.operatorLoss': 'Possible operator loss in the p expression ("p , .0001").',
	'reason.pOverOne': 'The p-value was read as greater than 1.',
	'field.psikoloji': 'psychology',
	'field.psychology': 'psychology',
	'field.health': 'health',
	'field.eğitim': 'education',
	'field.sosyal bilim': 'social science',
	'field.sağlık': 'health',

	// --------------------------------------------------------------- landing
	'meta.title': 'RepCheck — manuscript reporting audit',
	'meta.description':
		'RepCheck audits statistical reporting, citations, structure and claim language in manuscripts — entirely in your browser.',
	'brand.homeAria': 'RepCheck home',
	'nav.paste': 'Paste text',
	'nav.testSet': 'Test set',
	'nav.checks': 'Checks',
	'nav.privacy': 'Privacy',
	'nav.method': 'Methodology',
	'hero.kicker': 'Private academic pre-check',
	'hero.title': 'Catch inconsistencies before you submit.',
	'hero.lede':
		'Check statistical reporting, citations and structure. Review every finding alongside its evidence.',
	'hero.privacyTitle': 'Your research stays on your device.',
	'hero.privacyBody': 'Files are never uploaded to a server. Analysis runs in your browser.',
	'upload.single': 'Single document',
	'upload.batch': 'Batch',
	'upload.paste': 'Paste text instead',
	'upload.targetJournal': 'Target journal (optional)',
	'sample.cardTitle': 'From finding to evidence',
	'sample.tabFinding': 'Finding',
	'sample.tabSource': 'Source text',
	'sample.tabExplanation': 'Explanation',
	'sample.excerpt': 'Manuscript excerpt',
	'sample.reportedP': 'Reported p',
	'sample.recomputedP': 'Recomputed p',
	'sample.explanationTitle': 'Explanation',
	'sample.explanation': 'The reported p-value does not match the test statistic.',
	'sample.viewSource': 'View in source',
	'sample.evidenceTitle': 'Evidence in context',
	'sample.evidence':
		'We compared the two groups using an independent-samples t-test. The analysis yielded t(48) = 2.41, p = .12, indicating no statistically significant difference between the groups. The effect size was small (d = 0.34).',
	'sample.note': 'Illustrative example · not a scientific quality score',
	'checks.title': 'Clear checks. Traceable findings.',
	'band.title': 'Built for work you are not ready to share.',
	'band.body':
		'Document analysis happens locally in your browser. You choose when to export the report.',
	'band.link': 'How local processing works',
	'band.device': 'On your device',
	'toolkit.title': 'A growing academic toolkit',
	'toolkit.articles': 'Articles · available now',
	'toolkit.theses': 'Theses · planned',
	'toolkit.proposals': 'Research proposals · planned',
	'toolkit.method.title': 'Understand the method',
	'toolkit.method.body': 'See what each check covers and where manual review is needed.',
	'toolkit.method.link': 'Methodology',
	'toolkit.limits.title': 'Know the limits',
	'toolkit.limits.body':
		'Automated checks support your judgment. They do not replace peer review.',
	'toolkit.limits.link': 'Scope & limitations',
	'footer.about': 'About',
	'footer.privacy': 'Privacy',
	'footer.contact': 'Contact',
	'footer.claim': 'Academic rigor. Private by design.',
	'demo.quickLabel': 'Try it now:',
	'demo.button': 'Analyze the sample article',
	'demo.note': 'a demo article with deliberately planted errors',
	'demo.cta': 'Try the demo',
	'demo.ownCta': 'Try your own manuscript',
	'error.title': 'The document could not be processed',
	'error.retry': 'Try again',
	'error.demo': 'Continue with the demo',
	'paste.title': 'Paste text',
	'paste.hint': 'Paste the manuscript text you copied. At least 100 characters are required.',
	'paste.fileName': 'pasted-text',
	'paste.fileNameTxt': 'pasted-text.txt',
	'paste.placeholder': 'Abstract\nThis study ... t(48) = 2.41, p = .019 ...',
	'paste.chars': '{n} characters',
	'paste.analyze': 'Analyze',
	'checks.stat.title': 'Statistics',
	'checks.stat.body': 'Check the consistency of reported statistical results.',
	'checks.stat.detail': 'p-value recomputation · df verification · threshold flip detection',
	'checks.cite.title': 'Citations & references',
	'checks.cite.body': 'Find potential citation issues and reference mismatches.',
	'checks.cite.detail': 'In-text ↔ reference list matching · orphan and phantom citations',
	'checks.struct.title': 'Structure & reporting',
	'checks.struct.body': 'Check key reporting elements and overall structure.',
	'checks.struct.detail': 'CONSORT · STROBE · PRISMA checklist coverage',
	'checks.lang.title': 'Language & claims',
	'checks.lang.body': 'Flag unclear, overstated or unsupported claims.',
	'checks.lang.detail': 'Causal language in non-experimental designs · absolutist claims',
	'checks.access.title': 'Transparency & blind review',
	'checks.access.body': 'Check transparency statements and metadata for blind-review compliance.',
	'checks.access.detail': 'Ethics · COI · funding · data availability · metadata leak detection',
	'checks.prov.title': 'Text extraction quality',
	'checks.prov.body': 'Verify that the document text is read reliably for analysis.',
	'checks.prov.detail': 'PDF parse confidence · encoding verification',
	'metrics.label': 'Key numbers',
	'metrics.checks': 'automated check points',
	'metrics.engines': 'analysis engines',
	'metrics.local': 'client-side processing',
	'metrics.upload': 'files uploaded to any server',
	'why.title': 'Why RepCheck?',
	'why.privacy.title': 'Your data stays local',
	'why.privacy.body': 'Files never leave your browser. No uploads, no server processing, no third-party access.',
	'why.deterministic.title': 'Deterministic results',
	'why.deterministic.body': 'Rule-based checks produce the same findings every time. Auditable and reproducible.',
	'why.linked.title': 'Source-linked findings',
	'why.linked.body': 'Every finding points to the exact line in your manuscript. Click to see the evidence in context.',
	'sample.cta': "Try the sample document",
	'sample.cta.sub': "See finding counts without choosing a file",
	'meta.author.title': 'Author name in document metadata',
	'meta.author.detail': 'The PDF metadata contains an author name ("{name}"). This may compromise double-blind review.',
	'meta.author.suggestion': 'Remove the author field from the PDF properties before submission.',
	'meta.creator.title': 'Non-standard creator in metadata',
	'meta.creator.detail': 'The PDF creator field contains "{value}", which may identify the author.',
	'meta.email.title': 'Email address in manuscript body',
	'meta.email.detail': 'An email address appears in the manuscript text, which may compromise blind review.',
	'meta.email.suggestion': 'Remove email addresses if submitting for blind review.',
	'meta.orcid.title': 'ORCID identifier in manuscript',
	'meta.orcid.detail': 'An ORCID iD appears in the text, which identifies the author.',
	'stats.effectSize.title': 'No effect size reported',
	'stats.effectSize.detail':
		'The manuscript reports {count} test statistic(s) but no effect size measure (d, η², r², etc.) was found.',
	'stats.effectSize.suggestion':
		"Report an appropriate effect size alongside each test statistic (e.g. Cohen's d, partial η², R²).",
	'read.avgLength.title': 'High average sentence length',
	'read.avgLength.detail':
		'Average sentence length is {avg} words. Academic writing is typically clearest at 15–25 words.',
	'read.avgLength.suggestion': 'Consider breaking long sentences into shorter ones for clarity.',
	'read.longRatio.title': 'Many long sentences',
	'read.longRatio.detail': '{pct}% of sentences exceed 40 words.',
	'read.longRatio.suggestion': 'Aim for fewer than 25% of sentences over 40 words.',
	'read.passive.title': 'High passive voice ratio',
	'read.passive.detail': 'Approximately {pct}% of sentences use passive constructions.',
	'read.passive.suggestion': 'Consider using active voice where possible for directness.',
	'psych.reliability.title': 'No reliability information for measurement instrument',
	'psych.reliability.detail':
		"The manuscript uses a scale or questionnaire but does not report reliability (e.g. Cronbach's α, McDonald's ω, ICC).",
	'psych.reliability.suggestion': 'Report the reliability coefficient for each instrument used.',
	'psych.validity.title': 'No validity information for measurement instrument',
	'psych.validity.detail':
		'The manuscript uses a scale or questionnaire but does not mention validity (e.g. factor analysis, construct validity).',
	'psych.validity.suggestion': 'Consider reporting evidence of validity (e.g. CFA, convergent validity, AVE).',
	'hero.sampleKicker': 'Sample report',
	'hero.sampleTitle': 'Audit summary',
	'hero.samplePriority': '{n} priority findings',
	'hero.sampleVerify': '{n} need verification',
	'hero.sampleAreas': '{n} audit areas',
	'hero.sampleIssue': 'Statistical inconsistency',
	'hero.sampleIssueDetail': 'Reported .12 · recomputed ≈ .020',
	'hero.sampleReported': 'Reported',
	'hero.sampleRecomputed': 'Recomputed',
	'hero.sampleCite': 'Citation not found in references',
	'hero.sampleCiteQuote': '(Kaya, 2021)',
	'hero.sampleNote': 'Sample findings · not a scientific quality score',
	'hero.sampleCta': 'Open sample report',
	'trust.title': 'Unpublished work stays under your control.',
	'trust.sub':
		'Analysis happens on your device; your manuscript is never uploaded to a server.',
	'trust.local': 'Local processing',
	'trust.noUpload': 'No server upload',
	'trust.export': 'Export your report',
	'numbers.title': 'RepCheck in numbers',
	'numbers.engines': 'Engines',
	'numbers.enginesNote': '6 independent audit engines',
	'numbers.rules': 'Rules',
	'numbers.rulesNote': '200+ reporting checks',
	'numbers.privacy': 'Privacy',
	'numbers.privacyValue': '100%',
	'numbers.privacyNote': 'Your file never leaves the device',
	'auth.soonTitle': 'Sign-in is coming soon',
	'auth.soonBody': 'You can use RepCheck for free and anonymously right now.',
	'auth.soonClose': 'Continue',
	'sample.kicker': 'Sample report',
	'sample.title': 'A sample article with planted errors',
	'sample.bodyA':
		'A fictional article on social media and academic procrastination: a p-value contradiction, operator loss (',
	'sample.bodyB':
		'), a citation missing from the references, an uncited table, and causal language. See how the result is reported.',
	'sample.open': 'Open the sample',
	'privacy.title': 'Private by design',
	'privacy.body':
		"Your document, text and excerpts stay in your browser. PDF parsing and report generation run locally. We record numeric usage and result statistics; the last 20 report snapshots stay on this device.",
	'privacy.li1': 'No server, no account, no telemetry.',
	'privacy.li2': 'PDF text is extracted in a Web Worker; the interface never freezes.',
	'privacy.li3': 'Export reports as PDF, Markdown, CSV or JSON.',
	'footer.tagline': 'RepCheck · a client-side pre-submission audit tool',
	'footer.note': 'Not a peer-review verdict; low-confidence findings need manual verification.',

	// -------------------------------------------------------------- dropzone
	'drop.aria': 'Upload a manuscript: PDF, DOCX or LaTeX',
	'drop.headline': 'Choose file',
	'drop.subBefore': 'or drag it here',
	'drop.subAfter': '— PDF, Word (.docx), LaTeX (.tex) or plain text',
	'drop.limit': 'up to 40 MB',
	'drop.errorUnsupported':
		'This file type is not supported. Upload a PDF, DOCX, .tex or .txt file. (Legacy .doc files must be saved as .docx in Word.)',
	'drop.errorSize': 'The file is larger than 40 MB. Try a smaller file.',
	'drop.errorEmpty': 'The file appears to be empty.',

	// ------------------------------------------------------------ processing
	'proc.file': 'Text extraction',
	'proc.analyze': 'Engines running',
	'proc.report': 'Report',
	'proc.local': 'in-browser, no server',
	'proc.evaluating': 'evaluating findings',
	'proc.pageOf': 'page {page} of {total}',
	'proc.stage.starting': 'Starting',
	'proc.stage.preparing': 'Preparing the file',
	'proc.stage.pdfReading': 'Reading the PDF',
	'proc.stage.pdfOpened': 'PDF opened',
	'proc.stage.page': 'Reading page {page} of {total}',
	'proc.stage.workerFallback': 'Worker unavailable; trying on the main thread',
	'proc.stage.docxLoader': 'Loading the DOCX reader',
	'proc.stage.docxReading': 'Reading the DOCX',
	'proc.stage.paragraphs': 'Extracting paragraphs',
	'proc.stage.latexRead': 'Reading the LaTeX source',
	'proc.stage.latexParse': 'Parsing LaTeX',
	'proc.stage.textRead': 'Reading text',
	'proc.stage.analyzing': 'Analyzing the text',
	'proc.stage.report': 'Building the report',
	'proc.stage.demoPreparing': 'Preparing the demo article',
	'proc.stage.done': 'Done',

	// ----------------------------------------------------------- report shell
	'report.back': 'New document',
	'report.quality': 'extraction confidence: {level}',
	'report.qualityHigh': 'high',
	'report.qualityMedium': 'medium',
	'report.qualityLow': 'low',
	'report.exportHtml': 'HTML report',
	'report.tab.overview': 'Overview',
	'report.tab.findings': 'Findings',
	'report.tab.stats': 'Statistics',
	'report.tab.refs': 'Citations',
	'report.tab.structure': 'Structure & language',
	'report.tab.access': 'Access',
	'report.tab.method': 'Method',
	'access.title': 'Access and transparency',
	'access.lead':
		'Presence of data/code statements, declarations and identifier formats. Checks are local and format-only; no link is opened.',
	'access.score': 'Access score',
	'access.checksTitle': 'Statements and identifiers',
	'access.check.data': 'Data availability',
	'access.check.code': 'Code availability',
	'access.check.coi': 'Conflict of interest',
	'access.check.funding': 'Funding',
	'access.check.ethics': 'Ethics approval',
	'access.check.doi': 'DOI formats',
	'access.check.url': 'URL formats',
	'access.status.ok': 'provided',
	'access.status.missing': 'missing',
	'access.status.invalid': 'invalid',
	'access.status.not_applicable': 'n/a',
	'access.status.na': 'n/a',
	'access.human': 'Human-participant study detected',
	'access.computational': 'Computational study detected',
	'access.findings': '{n} access findings',
	'access.allGood': 'All expected statements are present and identifier formats look valid.',
	'access.detected': 'Detected: {text}',
	'access.note': 'No network requests are made; only local pattern checks.',
	'guideline.title': 'Reporting guideline',
	'guideline.detected': '{name} applies — {present}/{total} key items found.',
	'guideline.missing': 'missing',
	'guideline.present': 'found',
	'guideline.none':
		'No CONSORT/STROBE/PRISMA guideline matched this study type; only general structure checks were applied.',
	'guideline.name.consort': 'CONSORT',
	'guideline.name.strobe': 'STROBE',
	'guideline.name.prisma': 'PRISMA',
	'guideline.type.rct': 'Randomised controlled trial',
	'guideline.type.observational': 'Observational study',
	'guideline.type.systematic-review': 'Systematic review / meta-analysis',
	'guideline.type.unknown': 'Unclear study type',
	'report.compare': 'Compare with previous',
	'history.eyebrow': 'Report history',
	'history.title': 'Choose a previous report',
	'history.sub': 'Stored only in this browser — the last 20 reports are kept.',
	'history.empty': 'No previous reports yet. Analyse the revised manuscript to compare.',
	'history.issues': '{n} findings',
	'diff.eyebrow': 'Version comparison',
	'diff.title': 'Previous vs current report',
	'diff.previous': 'Previous',
	'diff.current': 'Current',
	'diff.categories': 'Category scores',
	'diff.category': 'Category',
	'diff.change': 'Change',
	'diff.fixed': 'Fixed ({n})',
	'diff.added': 'New ({n})',
	'diff.ongoing': 'Ongoing ({n})',
	'diff.none': 'None',
	'report.download': 'Download report (PDF via print)',
	'report.pdf': 'PDF',
	'report.markdown': 'Copy Markdown to clipboard',
	'report.md': 'MD',
	'report.copied': 'Copied',
	'print.score': 'Score',
	'print.categories': 'Category scores',
	'print.category': 'Category',
	'print.weight': 'Weight',
	'print.issues': 'Findings',
	'print.footer': 'Generated by RepCheck — repcheck.pages.dev',
	'export.mdTitle': 'RepCheck Report',
	'export.mdScore': 'Score',
	'export.mdDate': 'Date',
	'export.mdIssues': 'Issues',
	'export.mdEmpty': 'No findings.',
	'revision.short': 'Letter',
	'revision.title': 'Generate revision letter',
	'revision.eyebrow': 'Response to reviewers',
	'revision.sub': '{n} decisive findings will be included.',
	'revision.letterTitle': 'Revision letter',
	'revision.copy': 'Copy letter',
	'revision.copied': 'Copied',
	'revision.regenerate': 'Regenerate',
	'revision.manualHint': 'Edited manually — regenerate to overwrite.',
	'revision.empty': 'No decisive findings to include in the letter.',
	'revision.letter.greeting': 'Dear Editor,',
	'revision.letter.intro':
		'Thank you for the constructive feedback. We have carefully addressed each point raised during the review process. Below is a detailed response to the automated pre-submission check findings.',
	'revision.letter.closing':
		'We believe these revisions strengthen the manuscript. We look forward to your further guidance.',
	'revision.letter.signoff': 'Sincerely,',
	'revision.letter.authors': '[Authors]',
	'revision.letter.finding': 'Finding',
	'revision.letter.response': 'Response',
	'revision.letter.status': 'Status',
	'revision.letter.placeholder': 'Describe your correction…',
	'revision.letter.addressed': '{n} issues addressed',
	'revision.status.corrected': 'Corrected',
	'revision.status.clarified': 'Clarified',
	'revision.status.no_change': 'No change needed',
	'revision.status.acknowledged': 'Acknowledged',
	'batch.modeAria': 'Analysis mode',
	'batch.single': 'Single analysis',
	'batch.batch': 'Batch analysis',
	'batch.eyebrow': 'Batch analysis',
	'batch.title': 'Analyse multiple manuscripts',
	'batch.lede': 'Select up to {n} files. Each is analysed in your browser; nothing is uploaded.',
	'batch.drop': 'Drop manuscripts here',
	'batch.or': 'or',
	'batch.chooseFiles': 'Choose files',
	'batch.chooseFolder': 'Choose folder',
	'batch.formats': 'PDF, DOCX, TeX, TXT — max {n} files, 40 MB each',
	'batch.errorUnsupported': '{n} file(s) skipped: unsupported format.',
	'batch.errorSize': '{n} file(s) skipped: empty or larger than 40 MB.',
	'batch.errorLimit': 'Maximum {n} files can be analysed at once.',
	'batch.remove': 'Remove file',
	'batch.selected': '{n}/{max} selected',
	'batch.start': 'Analyse {n} files',
	'batch.kb': '{n} KB',
	'batch.progressTitle': 'Analysing batch',
	'batch.progressSub': '{done}/{total} finished',
	'batch.cancelAll': 'Cancel all',
	'batch.cancel': 'Cancel',
	'batch.status.queued': 'queued',
	'batch.status.extracting': 'extracting',
	'batch.status.analyzing': 'analysing',
	'batch.status.done': 'done',
	'batch.status.error': 'error',
	'batch.status.cancelled': 'cancelled',
	'batch.summaryTitle': 'Batch summary',
	'batch.summarySub': '{done}/{total} analysed',
	'batch.csv': 'CSV',
	'batch.json': 'JSON',
	'batch.new': 'New batch',
	'batch.previous': 'Compared with previous run {id}',
	'batch.partial': '{failed} failed, {cancelled} cancelled.',
	'batch.sort': 'Sort by score',
	'batch.sortDesc': 'Score ↓',
	'batch.sortAsc': 'Score ↑',
	'batch.filterGrade': 'Grade',
	'batch.allGrades': 'All grades',
	'batch.file': 'File',
	'batch.score': 'Score',
	'batch.grade': 'Grade',
	'batch.change': 'Change',
	'batch.open': 'Open',
	'batch.empty': 'No completed analyses match this filter.',
	'journal.label': 'Target journal',
	'journal.placeholder': 'Search journals…',
	'journal.none': 'Not specified',
	'journal.clear': 'Clear journal',
	'journal.selected': 'Selected',
	'journal.noneHint': 'No journal selected — only general checks run.',
	'journal.noResults': 'No matching journal.',
	'journal.rulesTitle': 'Journal rules',
	'journal.compliance': '{passed}/{total} journal checks passed (not included in the RepCheck score).',
	'journal.pass': 'pass',
	'journal.fail': 'needs attention',
	'journal.check.wordLimit': 'Length is {actual} words; the journal target is at most {max}.',
	'journal.check.requiredSection': 'The {sectionLabel} section is required.',
	'journal.check.requiredStatement': 'Required statement: {statementLabel}.',
	'journal.check.referenceStyle': 'References look like {detected}; target style: {style}.',
	'journal.check.formatRule': '{ruleLabel} is expected.',
	'journal.statement.data': 'data availability',
	'journal.statement.coi': 'conflict of interest',
	'journal.statement.funding': 'funding',
	'journal.statement.ethics': 'ethics approval',
	'journal.statement.author_contributions': 'author contributions',
	'journal.statement.registration': 'trial registration',
	'journal.rule.structured_abstract': 'A structured abstract',
	'journal.rule.imrad': 'IMRaD structure',
	'journal.rule.keywords': 'Keywords',
	'journal.ref.numbered': 'numbered (Vancouver/IEEE)',
	'journal.ref.author-year': 'author–year (APA/Harvard)',
	'journal.ref.unknown': 'unrecognised',
	'badge.short': 'Badge',
	'badge.eyebrow': 'Verification badge',
	'badge.title': 'Get a “Checked with RepCheck” badge',
	'badge.sub': 'Add this badge to your manuscript, preprint or profile.',
	'badge.id': 'Badge ID',
	'badge.note': 'Self-reported. Independent verification is coming soon.',
	'badge.light': 'Light',
	'badge.dark': 'Dark',
	'badge.small': 'Small',
	'badge.large': 'Large',
	'badge.downloadSvg': 'Download SVG',
	'badge.downloadPng': 'Download PNG',
	'badge.html': 'HTML embed',
	'badge.markdown': 'Markdown',
	'badge.text': 'Plain text',
	'badge.copy': 'Copy',
	'badge.copied': 'Copied',
	'badge.loading': 'Generating badge…',
	'auth.eyebrow': 'Accounts',
	'auth.title': 'Sign in to RepCheck',
	'auth.subtitle': "Sign in with your LibEdge account to open detailed reports. An active RepCheck assignment is required.",
	'auth.orcid': 'Continue with ORCID',
	'auth.google': 'Continue with Google',
	'auth.libedge': 'Sign in with your LibEdge Account',
	'auth.or': 'or use a magic link',
	'auth.email': 'Email',
	'auth.code': '6-digit code',
	'auth.sent': 'Check your inbox — the link and code expire in 10 minutes.',
	'auth.verify': 'Verify code',
	'auth.sendLink': 'Send magic link',
	'auth.note': "Your document and its text stay on your device. Account information and numeric usage/results are stored to manage access and statistics.",
	'auth.error.generic': 'Sign-in failed. Please try again.',
	'auth.error.send': 'Could not send the email. Try again later.',
	'auth.error.code': 'Invalid or expired code.',
	'auth.signIn': 'Sign in',
	'auth.signOut': 'Sign out',
	'auth.plan.free': 'Free',
	'auth.plan.pro': 'Pro',
	'auth.limit.title': 'Monthly limit reached',
	'auth.limit.body': 'Free accounts can analyse 5 manuscripts per month. Upgrade to Pro for unlimited analyses.',
	'auth.limit.upgrade': 'Upgrade to Pro',
	'auth.limit.later': 'Not now',
	'auth.sync.note': 'Sign in to keep your reports across devices.',
	'journal.style.vancouver': 'Vancouver',
	'journal.style.ieee': 'IEEE',
	'journal.style.apa': 'APA',
	'journal.style.harvard': 'Harvard',
	'report.hideText': 'Hide text',
	'report.showText': 'Show text',
	'report.charsK': '{n}k characters',
	'report.tabsAria': 'Report sections',

	// -------------------------------------------------------------- overview
	'overview.eyebrow': 'Overall assessment',
	'overview.critical': '{n} critical',
	'overview.warning': '{n} warnings',
	'overview.info': '{n} notes',
	'overview.undecided':
		'{n} findings were excluded from the verdict because of low confidence; they need manual verification.',
	'overview.verdict.clean':
		'<strong>Reporting is largely consistent.</strong> No critical contradiction was found; only minor improvements are suggested.',
	'overview.verdict.critical':
		'<strong>{n} critical finding(s)</strong> require the statistical reporting or the reference list to be verified. Do not submit before confirming them against the raw data.',
	'overview.verdict.good':
		'<strong>Generally good.</strong> A few reporting gaps remain; they can be fixed quickly.',
	'overview.verdict.medium':
		'<strong>Moderate issues.</strong> Several warnings co-occur; the reporting standard should be reviewed.',
	'overview.verdict.bad':
		'<strong>Serious reporting problems.</strong> Overall score {score} (grade {grade}); findings must be addressed one by one.',
	'overview.verdict.insufficient':
		'<strong>Insufficient coverage.</strong> Too few audit categories could be checked, so no letter grade is given. Review the findings manually.',
	'overview.coverage': '{checked}/{total} audit categories checked.',
	'overview.insufficientCoverage':
		'{checked}/{total} categories checked — coverage below 50%, grade withheld.',
	'overview.qualityEyebrow': 'Text extraction confidence',
	'overview.qualityOk': 'The text layer looks healthy; findings rest on the extracted text.',
	'overview.categoriesTitle': 'Category scores',
	'overview.categoriesSub': 'Scores are reduced only by high/medium-confidence findings.',
	'overview.priorityTitle': 'Priority findings',
	'overview.prioritySub': 'The {n} most important findings that count toward the verdict.',
	'overview.priorityNone': 'No critical or warning-level finding counts toward the verdict.',
	'overview.coverageTitle': 'Coverage',
	'overview.covStats': 'Statistical expressions',
	'overview.covStatsNote': '{consistent} consistent · {inconsistent} contradictory',
	'overview.covCitations': 'In-text citations',
	'overview.covCitationsNote': '{matched} matched · {missing} missing',
	'overview.covReferences': 'Reference entries',
	'overview.covReferencesNote': '{orphans} uncited',
	'overview.covFigures': 'Tables / figures',
	'overview.covFiguresNote': '{unreferenced} unreferenced',
	'overview.covLanguage': 'Language flags',
	'overview.covLanguageNote': '{causal} unjustified causal',
	'overview.design': 'Study design:',

	// -------------------------------------------------------------- findings
	'findings.all': 'All',
	'findings.category': 'Category',
	'findings.searchLabel': 'Search',
	'severity.label': 'Severity',
	'findings.critical': 'Critical',
	'findings.warning': 'Warning',
	'findings.info': 'Info',
	'findings.categoryAll': 'All categories',
	'findings.search': 'Search findings…',
	'findings.includeUndecided': 'Include findings excluded from the verdict',
	'findings.showing': '{n} findings shown',
	'findings.showingUndecided': ' · {n} excluded from the verdict',
	'findings.empty': 'No findings match this filter.',
	'findings.none': 'No findings.',
	'findings.legend': 'Severity legend',
	'findings.legendText': '{label}: {hint}',

	// ------------------------------------------------------------- issue card
	'issue.notDecisive': 'not counted in verdict',
	'issue.suggestion': 'Suggestion',
	'issue.showInText': 'Show in text',
	'issue.confidenceTitle': 'Confidence: {level}',
	'issue.lowConfidence': 'low confidence',

	// ----------------------------------------------------------------- stats
	'stats.total': 'statistical expressions',
	'stats.consistent': 'consistent',
	'stats.inconsistent': 'contradiction / deviation',
	'stats.unverifiable': 'unverifiable',
	'stats.empty':
		'No statistical expression was found. The tool looks for patterns such as t(48) = 2.41, p = .019.',
	'stats.emptyMono': 't(48) = 2.41, p = .019',
	'stats.col.expression': 'Expression',
	'stats.col.value': 'Value',
	'stats.col.df': 'df / n',
	'stats.col.reported': 'Reported p',
	'stats.col.computed': 'Computed p',
	'stats.col.status': 'Status',
	'stats.col.confidence': 'Confidence',
	'stats.status.consistent': 'Consistent',
	'stats.status.flip': 'Contradiction',
	'stats.status.deviation': 'Deviation',
	'stats.status.unverifiable': 'Unverifiable',
	'stats.status.uncomputable': 'Not computable',
	'stats.delta': 'Difference:',
	'stats.boundNote': 'the reported value is a bound (p < .001)',
	'stats.showInText': 'Show in text',
	'stats.claimsTitle': 'Significance claims',
	'stats.claimsHint':
		'Whether “significant” statements carry numerical support in the same sentence.',
	'stats.supported': 'supported',
	'stats.unsupported': 'unsupported',
	'stats.negated': 'negated',
	'stats.methodTitle': 'How are p-values recomputed?',
	'stats.methodT': 't → Student t distribution, df from t(·), two-tailed.',
	'stats.methodF': 'F → central F distribution, F(df1, df2), upper tail.',
	'stats.methodChi': 'χ² → chi-square distribution, χ²(df), upper tail.',
	'stats.methodR': 'r → t = r√((n−2)/(1−r²)), df = n−2, two-tailed.',
	'stats.methodZ': 'z → standard normal distribution, two-tailed.',
	'stats.tolerance':
		'Tolerance: 0.3 log₁₀ units (about 2×) or 0.005 absolute. Larger differences are “deviations”; differences that cross the significance threshold are “contradictions”. Low-confidence expressions are never reported as verdicts, even when they contradict.',
	'stats.notComputable': 'not computable',
	'stats.twoTailed': 'two-tailed',
	'stats.oneTailed': 'one-tailed',
	'stats.pShort': 'p = {p}',

	// ------------------------------------------------------------ references
	'refs.citations': 'in-text citations',
	'refs.references': 'reference entries',
	'refs.matched': 'matched',
	'refs.missing': 'missing from references',
	'refs.orphans': 'uncited',
	'refs.noSectionTitle': 'Reference section not found.',
	'refs.noSectionBody':
		'No heading such as “References”, “Bibliography” or “Kaynakça” was found, so citations could not be cross-checked.',
	'refs.noSectionCitations': '{n} citations exist in the text but could not be verified.',
	'refs.tab.matches': 'Matches',
	'refs.tab.missing': 'Missing / mismatched',
	'refs.tab.orphans': 'Uncited references',
	'refs.col.citation': 'In-text citation',
	'refs.col.status': 'Status',
	'refs.col.match': 'Reference match',
	'refs.status.matched': 'matched',
	'refs.status.missing': 'not in references',
	'refs.status.year': 'year mismatch',
	'refs.status.author': 'author mismatch',
	'refs.similar': 'Similar entries:',
	'refs.emptyMatches': 'No in-text citations found.',
	'refs.emptyMissing': 'Every in-text citation matches the reference list.',
	'refs.emptyOrphans': 'Every reference entry is cited in the text.',
	'refs.uncited': 'not cited in text',
	'refs.showInText': 'Show in text',
	'refs.etal': 'et al.',
	'refs.viewAria': 'Citation view',
	'refs.yearArrow': 'year: {from} → {to}',
	'refs.methodTitle': 'How does matching work?',
	'refs.methodBody':
		'In-text citations (Yılmaz, 2019; (Smith & Jones, 2020); Öztürk et al., 2021) are reduced to a surname + year key. Reference entries are parsed the same way. Surnames are compared with Jaro-Winkler similarity (threshold 0.86); a year that does not match exactly is reported as a “year mismatch”, and a surname that does not match as an “author mismatch”. Unmatched citations are critical; unused references are warnings.',

	// ------------------------------------------------------------- structure
	'struct.sectionsTitle': 'Section map',
	'struct.sectionsSub': 'The IMRaD structure is expected; headings may be numbered or plain.',
	'struct.section.abstract': 'Abstract',
	'struct.section.keywords': 'Keywords',
	'struct.section.introduction': 'Introduction',
	'struct.section.method': 'Method',
	'struct.section.results': 'Results',
	'struct.section.discussion': 'Discussion',
	'struct.section.references': 'References',
	'struct.section.appendix': 'Appendix',
	'struct.found': 'found',
	'struct.notFound': 'not found',
	'struct.detail': 'Details',
	'struct.gaps': 'Numbering gaps:',
	'struct.figuresTitle': 'Tables and figures',
	'struct.figuresSub': 'Caption ↔ in-text reference cross-check.',
	'struct.tables': 'Tables',
	'struct.figures': 'Figures',
	'struct.tableNo': 'Table {n}',
	'struct.figureNo': 'Figure {n}',
	'struct.referenced': 'referenced',
	'struct.unreferenced': 'unreferenced',
	'struct.noFigures': 'No table or figure caption was found.',
	'struct.dangling': 'Referenced in text but no caption:',
	'struct.languageTitle': 'Language and claim strength',
	'struct.languageSub':
		'Causal language, overclaims and significance claims without a test. Study design:',
	'struct.designEvidence': 'evidence:',
	'struct.emptyLang': 'No notable language pattern was found.',
	'struct.lang.causal': 'causal',
	'struct.lang.causalJustified': 'causal (supported by design)',
	'struct.lang.absolutist': 'overclaim',
	'struct.lang.generalization': 'generalization',
	'struct.lang.significance': 'unsupported significance',

	// -------------------------------------------------------------- method
	'method.title': 'How it works',
	'method.leadA':
		'RepCheck processes your manuscript',
	'method.leadStrong': 'entirely in your browser',
	'method.leadB':
		'. The file is never sent to a server; refreshing the page clears it from memory. The goal is to surface reporting inconsistencies an editor could catch before submission — an',
	'method.leadEm': 'audit, not a verdict',
	'method.pipe.extract': 'Extraction',
	'method.pipe.parse': 'Parsing',
	'method.pipe.cross': 'Cross-check',
	'method.pipe.confidence': 'Confidence filter',
	'method.pipe.score': 'Score + annotation',
	'method.pipelineAria': 'Pipeline',
	'method.confColLevel': 'Confidence',
	'method.confColCond': 'Typical condition',
	'method.confColEffect': 'Effect on score',
	'method.extractTitle': '1 · Text extraction',
	'method.extractPdf': 'PDF — pdf.js, in a separate Web Worker, page by page. Every text line keeps its bounding box.',
	'method.extractDocx': 'DOCX — mammoth.js returns raw paragraph text; there is no page geometry.',
	'method.extractTex': 'LaTeX — parsed from source; section, table/figure and reference structure is most reliable here.',
	'method.operatorNote':
		'Operator loss is a real problem in PDFs: p < .0001 can come out as p , .0001. The engine flags these patterns separately and',
	'method.operatorNoteStrong': 'never uses them for a verdict',
	'method.confTitle': '2 · Confidence score',
	'method.confBody':
		'Every finding carries high / medium / low confidence. Only high and medium findings affect the score and the verdict. Low-confidence findings stay in the list but are marked as',
	'method.confBodyStrong': 'not counted',
	'method.confHigh': 'high',
	'method.confHighCond': 'Explicit statistic + df/n + p, clean operator',
	'method.confHighEffect': 'full penalty',
	'method.confMedium': 'medium',
	'method.confMediumCond': 'Missing p, missing df, comma decimal',
	'method.confMediumEffect': '65% penalty',
	'method.confLow': 'low',
	'method.confLowCond': 'Operator loss, impossible value, ambiguity',
	'method.confLowEffect': 'no penalty',
	'method.confNote':
		'If the document’s overall text quality is low (scanned PDF, broken font), every finding’s confidence is lowered by one level.',
	'method.statsTitle': '3 · Statistics audit',
	'method.statsBody':
		'Expressions such as “t(48) = 2.41, p = .019” are parsed; degrees of freedom and sample size are extracted. The p-value is',
	'method.statsBodyStrong': 'recomputed',
	'method.statsBodyEnd': 'with jStat and compared to the reported value:',
	'method.statsFlip': 'Contradiction — one is p < .05, the other ≥ .05. Critical.',
	'method.statsDev': 'Deviation — same decision, but the difference is large (log₁₀ > 0.3). Warning.',
	'method.statsNoP': 'No p — a statistic is reported without a p-value. Warning.',
	'method.statsOp': 'Operator loss — p , .05. Warning, not counted.',
	'method.statsT': 't → p = 2 · P(T > |t|), df',
	'method.statsF': 'F → p = P(F > f), df₁, df₂',
	'method.statsChi': 'χ² → p = P(χ² > x), df',
	'method.statsR': 'r → t = r√((n−2)/(1−r²))',
	'method.statsZ': 'z → p = 2 · P(Z > |z|)',
	'method.refTitle': '4 · Citations and references',
	'method.refBody':
		'In-text citations (parenthetical and narrative) are matched to reference entries by surname + year. Surnames are compared with Jaro-Winkler similarity (threshold 0.86); Turkish characters are folded. A citation absent from the references is critical; a reference never cited is a warning.',
	'method.langTitle': '5 · Structure and language',
	'method.langBodyA':
		'IMRaD sections, table/figure captions and their in-text references are cross-checked. Causal language (',
	'method.langBodyMonoA': 'caused',
	'method.langBodyB': ', ',
	'method.langBodyMonoB': 'affects',
	'method.langBodyC':
		') is weighed against the study design extracted from the method section: legitimate in an experimental design, an overclaim in a correlational one. If the design cannot be determined, the finding stays at the',
	'method.langBodyEm': 'info',
	'method.langBodyD': 'level.',
	'method.scoreTitle': '6 · Weighted score',
	'method.scoreBody':
		'Category scores start at 100; each decisive finding subtracts by severity (critical 16, warning 7, info 2.5) and confidence weight. To keep dozens of repetitions of the same issue from driving the score to zero, the effect diminishes after six findings. The overall score is the weighted average.',
	'method.scoreThresholds':
		'Thresholds: <strong>A</strong> ≥ 90, <strong>B</strong> ≥ 75, <strong>C</strong> ≥ 60, otherwise <strong>D</strong>.',
	'method.limitsTitle': 'Limitations',
	'method.limit1': 'This tool is <strong>not a peer reviewer</strong>. Findings concern internal consistency; it has no access to raw data or ethics processes.',
	'method.limit2': 'Scanned PDFs (no text layer) cannot be analysed. An OCR version is required.',
	'method.limit3': 'If the reference style is very irregular, entry parsing may be partial; citation results then carry a “low confidence” flag.',
	'method.limit4': 'Statistical expressions are recognised only in standard APA-like patterns; unusual abbreviations may be missed.',
	'method.limit5': 'Language heuristics are context-sensitive; they point, they do not judge.',
	'method.logToggle': 'Analysis log',
	'method.logFile': 'File',
	'method.logFormat': 'Format',
	'method.logPages': 'Pages',
	'method.logChars': 'Characters',
	'method.logQuality': 'Text quality',
	'method.logOperators': 'Operator suspects',
	'method.logBlocks': 'Paragraphs / blocks',
	'method.logCreated': 'Generated',
	'method.logNotes': 'Notes:',
	'method.appendixTitle': 'Finding log',
	'method.appendixBody':
		'The full list of findings, with line-level annotation. Low-confidence findings are marked separately.',
	'method.appendixUndecided': 'excluded from the verdict',

	// --------------------------------------------------------------- viewer
	'viewer.title': 'Document view',
	'viewer.lines': '{n} lines',
	'viewer.pages': '{n} pages',
	'viewer.annotated': '{n} annotated positions',
	'viewer.highlight': 'Highlight:',
	'viewer.countAria': '{n} findings',
	'viewer.flagAria': 'findings on this line',

	// ------------------------------------------------------------- test set
	'testSet.kicker': 'Validation',
	'testSet.title': '25-article test set',
	'testSet.body':
		'The manifest schema lives in src/lib/data/testSet.ts. These titles are fictional placeholders; replace them with your own set. Each article records the main risk it is expected to contain, so you can compare whether the engine caught it.',
	'testSet.inspected': 'inspected',
	'testSet.colArticle': 'Article',
	'testSet.colId': 'ID',
	'testSet.colField': 'Field',
	'testSet.colFocus': 'Focus',
	'testSet.foot': 'Marks are stored only in this browser (localStorage); they are never sent anywhere.',
	'testSet.markAria': 'Mark {id} as inspected',

	// ---------------------------------------------------------------- errors
	'err.unsupported':
		'This file type is not supported. Upload a PDF, DOCX or .tex file. (Legacy .doc files are not supported.)',
	'err.docx': 'The DOCX could not be read: {message}. The file may be corrupt or an old .doc; save it as .docx in Word.',
	'err.docxEmpty': 'No text was found inside the DOCX; the document may contain only images.',
	'err.pdf':
		'The PDF could not be read. The file may be encrypted or corrupt. If it is a scanned PDF, it has no text layer.',
	'err.pdfWorker': 'The PDF worker failed: {message}',
	'err.pdfIncomplete': 'PDF extraction did not complete.',
	'err.empty': 'The file appears to be empty.',
	'err.tooShort': 'At least 100 characters of text are required for analysis.',
	'err.generic': 'Something went wrong while processing the document.',
	'export.title': 'RepCheck report',
	'export.summary': '{critical} critical · {warning} warnings · {undecided} excluded from the verdict',
	'export.colCategory': 'Category',
	'export.colScore': 'Score',
	'export.colWeight': 'Weight',
	'export.colNote': 'Note',
	'export.findingsCount': '{n} findings',
	'export.confidence': 'confidence: {level}',
	'export.footer':
		'This report is an automated pre-submission audit, not a verdict. Low-confidence findings require manual verification.',
	'export.csv.category': 'category',
	'export.csv.severity': 'severity',
	'export.csv.code': 'code',
	'export.csv.title': 'title',
	'export.csv.detail': 'detail',
	'export.csv.confidence': 'confidence',
	'export.csv.decisive': 'counted_in_verdict',
	'export.csv.page': 'page',
	'export.csv.line': 'line',
	'export.csv.quote': 'quote'
};

const tr: Dict = {
"preview.title": "Ön kontrolünüz hazır",
"preview.eyebrow": "Mahremiyet odaklı belge kontrolü",
"preview.findings": "bulgu",
"preview.categories": "Kategorilere göre bulgular",
"preview.unlockTitle": "Ayrıntılı raporu açın",
"preview.unlockBody": "Açıklamaları, konumları ve önerileri görmek için aktif RepCheck ataması olan LibEdge hesabınızla giriş yapın. Belgeniz bu sekmede kalır.",
"preview.waiting": "Yeni sekmede girişi tamamlayıp buraya dönün. Analiziniz sizi bekliyor.",
"preview.privacy": "Belge ve metni cihazınızda kalır. Yalnızca sayısal kullanım ve sonuç istatistikleri kaydedilir.",
"preview.another": "Başka bir belgeyi kontrol et",
"preview.complete": "Giriş tamamlandı",
"preview.return": "Ayrıntılı rapor için analiz sekmenize dönün. Bu sekmeyi kapatabilirsiniz.",
"preview.openApp": "RepCheck’i aç",
"preview.quota": "Aylık ayrıntılı rapor hakkınız doldu. Bu ön kontroldeki bulgu sayılarını görüntüleyebilirsiniz.",
"preview.batchTitle": "LibEdge hesabınızla toplu analiz",
"preview.batchBody": "Toplu analiz için aktif RepCheck atamasıyla giriş yapın. Tek belge ön kontrolünü giriş yapmadan deneyebilirsiniz.",
"auth.plan.institution": "Kurum erişimi",
"productAccess.title":"RepCheck erişimi",
"productAccess.assignment":"LibEdge hesabınıza veya bağlı olduğunuz kuruma aktif RepCheck ürünü atanmış olması gerekir.",
"productAccess.help":"Erişimi doğrulamak için LibEdge hesabınızla giriş yapın. Ürünlerinizde RepCheck yoksa kurum yöneticinizle veya LibEdge ile iletişime geçin.",
"productAccess.unavailable":"RepCheck erişiminiz doğrulanamadı. Lütfen tekrar deneyin.",
"productAccess.expired":"Erişiminizi doğrulamak için LibEdge hesabınızla yeniden giriş yapın.",
"productAccess.retry":"Erişimi yeniden kontrol et",
"productAccess.checking":"Kontrol ediliyor…",
"productAccess.account":"LibEdge hesabım",
"productAccess.logout":"Çıkış yap",
	// ---------------------------------------------------------------- common
	'common.beta': 'beta',
	'common.language': 'Dil',
	'common.close': 'Kapat',
	'common.weight': 'ağırlık %{n}',
	'common.page': 's. {n}',
	'common.line': 'satır {n}',
	'common.chars': 'karakter',
	'common.hide': 'Gizle',
	'common.show': 'Göster',
	'common.yes': 'evet',
	'common.no': 'hayır',
	'severity.critical': 'Kritik',
	'severity.warning': 'Uyarı',
	'severity.info': 'Bilgi',
	'severity.ok': 'Tamam',
	'severity.critical.hint': 'Kararı doğrudan etkileyen, güçlü kanıtlı bulgu',
	'severity.warning.hint': 'Düzeltilmesi önerilen raporlama sorunu',
	'severity.info.hint': 'Stil/tercih düzeyinde not',
	'severity.ok.hint': 'Sorun tespit edilmedi',
	'category.statistics': 'İstatistiksel raporlama',
	'category.citations': 'Atıf ve kaynakça',
	'category.structure': 'Yapısal bütünlük',
	'category.language': 'Dil ve iddia düzeyi',
	'category.provenance': 'Çıkarım güvenilirliği',
	'category.access': 'Şeffaflık ve kör hakem',
	'category.short.statistics': 'İstatistik',
	'category.short.citations': 'Atıf',
	'category.short.structure': 'Yapı',
	'category.short.language': 'Dil',
	'category.short.provenance': 'Çıkarım',
	'category.short.access': 'Şeffaflık',
	'category.notChecked': 'kontrol edilemedi',
	'category.findings': '{n} bulgu',
	'category.criticals': '{n} kritik',
	'category.noData': 'veri yok',
	'confidence.high': 'Yüksek güven',
	'confidence.medium': 'Orta güven',
	'confidence.low': 'Düşük güven',
	'confidence.high.short': 'yüksek',
	'confidence.medium.short': 'orta',
	'confidence.low.short': 'düşük',
	'design.experimental': 'Deneysel',
	'design.quasi': 'Yarı deneysel',
	'design.correlational': 'İlişkisel / kesitsel',
	'design.case': 'Vaka çalışması',
	'design.unknown': 'Belirlenemedi',
	'time.justNow': 'az önce',
	'time.minutesAgo': '{n} dk önce',
	'time.hoursAgo': '{n} sa önce',
	'reason.decimalComma': 'Ondalık ayırıcı virgülle yazılmış; Türkçe biçim.',
	'reason.pMissing': 'Aynı ifadede raporlanmış p değeri bulunamadı.',
	'reason.dfMissingT': 't için serbestlik derecesi yok; p yeniden hesaplanamaz.',
	'reason.dfMissingF': 'F için serbestlik dereceleri eksik.',
	'reason.dfMissingChi': 'χ² için serbestlik derecesi yok.',
	'reason.nMissing': 'r için örneklem büyüklüğü yok.',
	'reason.negativeT': 'Negatif t değeri; deney/karşılaştırma yönü belirsiz.',
	'reason.impossible': 'Değer olanaklı aralığın dışında.',
	'reason.operatorLoss': 'p ifadesinde operatör kaybı olabilir ("p , .0001").',
	'reason.pOverOne': 'p değeri 1’den büyük okundu.',
	'field.psikoloji': 'psikoloji',
	'field.psychology': 'psikoloji',
	'field.health': 'sağlık',
	'field.eğitim': 'eğitim',
	'field.sosyal bilim': 'sosyal bilim',
	'field.sağlık': 'sağlık',

	// --------------------------------------------------------------- landing
	'meta.title': 'RepCheck — makale raporlama denetimi',
	'meta.description':
		'RepCheck; makalelerdeki istatistiksel raporlamayı, atıfları, yapıyı ve iddia dilini tamamen tarayıcınızda denetler.',
	'brand.homeAria': 'RepCheck ana sayfa',
	'nav.paste': 'Metin yapıştır',
	'nav.testSet': 'Test seti',
	'nav.checks': 'Kontroller',
	'nav.privacy': 'Gizlilik',
	'nav.method': 'Yöntem',
	'hero.kicker': 'Gizli akademik ön kontrol',
	'hero.title': 'Tutarsızlıkları göndermeden önce yakalayın.',
	'hero.lede':
		'İstatistiksel raporlamayı, atıfları ve yapıyı kontrol edin. Her bulguyu kanıtıyla birlikte inceleyin.',
	'hero.privacyTitle': 'Araştırmanız cihazınızda kalır.',
	'hero.privacyBody': 'Dosyalar sunucuya yüklenmez. Analiz tarayıcınızda çalışır.',
	'upload.single': 'Tek belge',
	'upload.batch': 'Toplu',
	'upload.paste': 'Metin yapıştır',
	'upload.targetJournal': 'Hedef dergi (isteğe bağlı)',
	'sample.cardTitle': 'Bulgu ve kanıt',
	'sample.tabFinding': 'Bulgu',
	'sample.tabSource': 'Kaynak metni',
	'sample.tabExplanation': 'Açıklama',
	'sample.excerpt': 'Makale alıntısı',
	'sample.reportedP': 'Raporlanan p',
	'sample.recomputedP': 'Yeniden hesaplanan p',
	'sample.explanationTitle': 'Açıklama',
	'sample.explanation': 'Raporlanan p-değeri test istatistiğiyle uyuşmuyor.',
	'sample.viewSource': 'Kaynakta göster',
	'sample.evidenceTitle': 'Bağlamdaki kanıt',
	'sample.evidence':
		'İki grubu bağımsız örneklem t-testiyle karşılaştırdık. Analiz t(48) = 2.41, p = .12 sonucunu verdi; gruplar arasında istatistiksel olarak anlamlı fark yoktu. Etki büyüklüğü küçüktü (d = 0.34).',
	'sample.note': 'Örnek gösterim · bilimsel kalite puanı değildir',
	'checks.title': 'Net kontroller. İzlenebilir bulgular.',
	'band.title': 'Paylaşmaya hazır olmadığınız çalışmalar için.',
	'band.body':
		'Belge analizi tarayıcınızda yerel olarak yapılır. Raporu ne zaman dışa aktaracağınıza siz karar verirsiniz.',
	'band.link': 'Yerel işleme nasıl çalışır',
	'band.device': 'Cihazınızda',
	'toolkit.title': 'Büyüyen bir akademik araç seti',
	'toolkit.articles': 'Makaleler · şimdi kullanılabilir',
	'toolkit.theses': 'Tezler · planlandı',
	'toolkit.proposals': 'Araştırma önerileri · planlandı',
	'toolkit.method.title': 'Yöntemi anlayın',
	'toolkit.method.body': 'Her kontrolün neyi kapsadığını ve nerede manuel inceleme gerektiğini görün.',
	'toolkit.method.link': 'Yöntem',
	'toolkit.limits.title': 'Sınırları bilin',
	'toolkit.limits.body':
		'Otomatik kontroller kararınızı destekler; hakem değerlendirmesinin yerini almaz.',
	'toolkit.limits.link': 'Kapsam ve sınırlar',
	'footer.about': 'Hakkında',
	'footer.privacy': 'Gizlilik',
	'footer.contact': 'İletişim',
	'footer.claim': 'Akademik titizlik. Tasarım gereği gizli.',
	'demo.quickLabel': 'Hemen deneyin:',
	'demo.button': 'Örnek makaleyi analiz et',
	'demo.note': 'hataları bilinçli yerleştirilmiş bir gösterim makalesi',
	'demo.cta': 'Demo ile dene',
	'demo.ownCta': 'Kendi makalenizi deneyin',
	'error.title': 'Belge işlenemedi',
	'error.retry': 'Tekrar dene',
	'error.demo': 'Örnekle devam et',
	'paste.title': 'Metin yapıştır',
	'paste.hint': 'Panoya kopyaladığınız makale metnini yapıştırın. En az 100 karakter gerekir.',
	'paste.fileName': 'yapıştırılan-metin',
	'paste.fileNameTxt': 'yapıştırılan-metin.txt',
	'paste.placeholder': 'Özet\nBu çalışmada ... t(48) = 2.41, p = .019 ...',
	'paste.chars': '{n} karakter',
	'paste.analyze': 'Analiz et',
	'checks.stat.title': 'İstatistik',
	'checks.stat.body': 'Raporlanan istatistiksel sonuçların tutarlılığını kontrol edin.',
	'checks.stat.detail': 'p-değeri yeniden hesaplama · df doğrulama · eşik geçişi tespiti',
	'checks.cite.title': 'Atıf ve kaynakça',
	'checks.cite.body': 'Olası atıf sorunlarını ve kaynakça uyuşmazlıklarını bulun.',
	'checks.cite.detail': 'Metin içi ↔ kaynakça eşleştirme · yetim ve hayalet atıflar',
	'checks.struct.title': 'Yapı ve raporlama',
	'checks.struct.body': 'Temel raporlama öğelerini ve genel yapıyı kontrol edin.',
	'checks.struct.detail': 'CONSORT · STROBE · PRISMA kontrol listesi kapsama oranı',
	'checks.lang.title': 'Dil ve iddialar',
	'checks.lang.body': 'Belirsiz, abartılı veya desteksiz iddiaları işaretleyin.',
	'checks.lang.detail': 'Deneysel olmayan desenlerde nedensellik dili · aşırı iddia',
	'checks.access.title': 'Şeffaflık ve kör hakem',
	'checks.access.body': 'Şeffaflık beyanlarını ve kör hakem uyumluluğunu kontrol edin.',
	'checks.access.detail': 'Etik · çıkar çatışması · fonlama · veri erişim · metadata sızıntı tespiti',
	'checks.prov.title': 'Metin çıkarım kalitesi',
	'checks.prov.body': 'Belge metninin analiz için güvenilir okunduğunu doğrulayın.',
	'checks.prov.detail': 'PDF ayrıştırma güveni · kodlama doğrulaması',
	'metrics.label': 'Temel rakamlar',
	'metrics.checks': 'otomatik kontrol noktası',
	'metrics.engines': 'analiz motoru',
	'metrics.local': 'istemci tarafında işleme',
	'metrics.upload': 'sunucuya yüklenen dosya',
	'why.title': 'Neden RepCheck?',
	'why.privacy.title': 'Verileriniz sizde kalır',
	'why.privacy.body': 'Dosyalarınız tarayıcınızdan çıkmaz. Yükleme yok, sunucu işleme yok, üçüncü taraf erişimi yok.',
	'why.deterministic.title': 'Deterministik sonuçlar',
	'why.deterministic.body': 'Kural tabanlı kontroller her seferinde aynı bulguları üretir. Denetlenebilir ve tekrarlanabilir.',
	'why.linked.title': 'Kaynağa bağlı bulgular',
	'why.linked.body': 'Her bulgu makalenizdeki tam satıra işaret eder. Tıklayarak kanıtı bağlamında görün.',
	'sample.cta': "Örnek belgeyle ön kontrolü deneyin",
	'sample.cta.sub': "Dosya seçmeden bulgu sayılarını görün",
	'meta.author.title': 'Belge üstverisinde yazar adı',
	'meta.author.detail': 'PDF üstverisi "{name}" yazar adını içeriyor. Bu, çift kör hakemlik sürecini bozabilir.',
	'meta.author.suggestion': 'Göndermeden önce PDF özelliklerinden yazar alanını kaldırın.',
	'meta.creator.title': 'Üstveride standart dışı oluşturucu',
	'meta.creator.detail': 'PDF oluşturucu alanı "{value}" değerini içeriyor ve yazarı tanımlayabilir.',
	'meta.email.title': 'Makale gövdesinde e-posta adresi',
	'meta.email.detail': 'Makale metninde bir e-posta adresi görünüyor ve kör hakemliği tehlikeye atabilir.',
	'meta.email.suggestion': 'Kör hakemlik için gönderim yapıyorsanız e-posta adreslerini kaldırın.',
	'meta.orcid.title': 'Makalede ORCID tanımlayıcısı',
	'meta.orcid.detail': 'Metinde bir ORCID iD görünüyor ve yazarı doğrudan tanımlıyor.',
	'stats.effectSize.title': 'Etki büyüklüğü raporlanmamış',
	'stats.effectSize.detail':
		'Makale {count} test istatistiği içeriyor ancak etki büyüklüğü ölçüsü (d, η², r² vb.) bulunamadı.',
	'stats.effectSize.suggestion':
		'Her test istatistiğinin yanına uygun bir etki büyüklüğü raporlayın (ör. Cohen d, kısmi η², R²).',
	'read.avgLength.title': 'Yüksek ortalama cümle uzunluğu',
	'read.avgLength.detail':
		'Ortalama cümle uzunluğu {avg} kelime. Akademik yazımda 15–25 kelime en okunaklı aralıktır.',
	'read.avgLength.suggestion': 'Okunabilirlik için uzun cümleleri bölmeyi düşünün.',
	'read.longRatio.title': 'Çok sayıda uzun cümle',
	'read.longRatio.detail': "Cümlelerin %{pct}'i 40 kelimeyi aşıyor.",
	'read.longRatio.suggestion': '40 kelimeyi aşan cümle oranını %25 altında tutmayı hedefleyin.',
	'read.passive.title': 'Yüksek edilgen çatı oranı',
	'read.passive.detail': "Cümlelerin yaklaşık %{pct}'i edilgen yapı kullanıyor.",
	'read.passive.suggestion': 'Mümkün olan yerlerde etken çatı kullanmayı düşünün.',
	'psych.reliability.title': 'Ölçme aracı için güvenirlik bilgisi yok',
	'psych.reliability.detail':
		'Makale bir ölçek veya anket kullanıyor ancak güvenirlik bilgisi (ör. Cronbach α, McDonald ω, ICC) raporlanmamış.',
	'psych.reliability.suggestion': 'Kullanılan her ölçme aracı için güvenirlik katsayısını raporlayın.',
	'psych.validity.title': 'Ölçme aracı için geçerlik bilgisi yok',
	'psych.validity.detail':
		'Makale bir ölçek veya anket kullanıyor ancak geçerlik bilgisinden (ör. faktör analizi, yapı geçerliği) bahsedilmemiş.',
	'psych.validity.suggestion': 'Geçerlik kanıtlarını raporlamayı düşünün (ör. DFA, yakınsak geçerlik, AVE).',
	'hero.sampleKicker': 'Örnek rapor',
	'hero.sampleTitle': 'Kontrol özeti',
	'hero.samplePriority': '{n} öncelikli bulgu',
	'hero.sampleVerify': '{n} doğrulama gerekli',
	'hero.sampleAreas': '{n} kontrol alanı',
	'hero.sampleIssue': 'İstatistiksel tutarsızlık',
	'hero.sampleIssueDetail': 'Raporlanan .12 · yeniden hesaplanan ≈ .020',
	'hero.sampleReported': 'Raporlanan',
	'hero.sampleRecomputed': 'Yeniden hesaplanan',
	'hero.sampleCite': 'Atıf kaynakçada bulunamadı',
	'hero.sampleCiteQuote': '(Kaya, 2021)',
	'hero.sampleNote': 'Örnek bulgular · Bilimsel kalite puanı değildir',
	'hero.sampleCta': 'Örnek raporu incele',
	'trust.title': 'Yayımlanmamış çalışmanız sizin kontrolünüzde.',
	'trust.sub':
		'Belge analizi cihazınızda gerçekleşir; dosyanız analiz için bir sunucuya yüklenmez.',
	'trust.local': 'Yerel belge işleme',
	'trust.noUpload': 'Sunucuya dosya yükleme yok',
	'trust.export': 'Raporu dışa aktarın',
	'numbers.title': 'Sayılarla RepCheck',
	'numbers.engines': 'Motor',
	'numbers.enginesNote': '6 bağımsız analiz motoru',
	'numbers.rules': 'Kural',
	'numbers.rulesNote': '200’den fazla kontrol kuralı',
	'numbers.privacy': 'Gizlilik',
	'numbers.privacyValue': '%100',
	'numbers.privacyNote': 'Dosyanız cihazınızdan çıkmaz',
	'auth.soonTitle': 'Giriş sistemi yakında aktif olacak',
	'auth.soonBody': 'Şimdilik RepCheck’i ücretsiz ve anonim olarak kullanabilirsiniz.',
	'auth.soonClose': 'Devam et',
	'sample.kicker': 'Örnek rapor',
	'sample.title': 'Hataları yerleştirilmiş örnek makale',
	'sample.bodyA':
		'Sosyal medya ve akademik erteleme üzerine kurgusal bir makale: p çelişkisi, operatör kaybı (',
	'sample.bodyB':
		'), kaynakçada olmayan atıf, atıfsız tablo ve nedensel dil. Sonucun nasıl raporlandığını görün.',
	'sample.open': 'Örneği aç',
	'privacy.title': 'Gizlilik, tasarım gereği',
	'privacy.body':
		"Belgeniz, metni ve alıntıları tarayıcınızda kalır. PDF ayrıştırma ve rapor üretimi cihazınızda çalışır. Sayısal kullanım ve sonuç istatistikleri kaydedilir; son 20 rapor özeti bu cihazda tutulur.",
	'privacy.li1': 'Sunucu yok, hesap yok, telemetri yok.',
	'privacy.li2': "PDF metni Web Worker'da çıkarılır; arayüz kilitlenmez.",
	'privacy.li3': 'Raporları PDF, Markdown, CSV veya JSON olarak dışa aktarın.',
	'footer.tagline': 'RepCheck · istemci taraflı ön denetim aracı',
	'footer.note': 'Kesin hakem kararı değildir; düşük güvenli bulgular manuel doğrulanmalıdır.',

	// -------------------------------------------------------------- dropzone
	'drop.aria': 'Makale dosyası yükle: PDF, DOCX veya LaTeX',
	'drop.headline': 'Dosya seçin',
	'drop.subBefore': 'veya buraya sürükleyin',
	'drop.subAfter': '— PDF, Word (.docx), LaTeX (.tex) veya düz metin',
	'drop.limit': 'en fazla 40 MB',
	'drop.errorUnsupported':
		'Bu dosya türü desteklenmiyor. PDF, DOCX, .tex veya .txt yükleyin. (.doc uzantılı eski Word dosyaları Word’de .docx olarak kaydedilmelidir.)',
	'drop.errorSize': 'Dosya 40 MB’tan büyük. Daha küçük bir dosya deneyin.',
	'drop.errorEmpty': 'Dosya boş görünüyor.',

	// ------------------------------------------------------------ processing
	'proc.file': 'Metin çıkarımı',
	'proc.analyze': 'Motorlar çalışıyor',
	'proc.report': 'Rapor',
	'proc.local': 'tarayıcıda, sunucusuz',
	'proc.evaluating': 'bulgular değerlendiriliyor',
	'proc.pageOf': 'sayfa {page} / {total}',
	'proc.stage.starting': 'Başlıyor',
	'proc.stage.preparing': 'Dosya hazırlanıyor',
	'proc.stage.pdfReading': 'PDF okunuyor',
	'proc.stage.pdfOpened': 'PDF açıldı',
	'proc.stage.page': 'Sayfa {page}/{total} okunuyor',
	'proc.stage.workerFallback': 'Worker kullanılamıyor, ana thread’de deneniyor',
	'proc.stage.docxLoader': 'DOCX okuyucusu yükleniyor',
	'proc.stage.docxReading': 'DOCX okunuyor',
	'proc.stage.paragraphs': 'Paragraflar çıkarılıyor',
	'proc.stage.latexRead': 'LaTeX kaynağı okunuyor',
	'proc.stage.latexParse': 'LaTeX ayrıştırılıyor',
	'proc.stage.textRead': 'Metin okunuyor',
	'proc.stage.analyzing': 'Metin analiz ediliyor',
	'proc.stage.report': 'Rapor hazırlanıyor',
	'proc.stage.demoPreparing': 'Örnek makale hazırlanıyor',
	'proc.stage.done': 'Tamamlandı',

	// ----------------------------------------------------------- report shell
	'report.back': 'Yeni belge',
	'report.quality': 'çıkarım güvenilirliği: {level}',
	'report.qualityHigh': 'yüksek',
	'report.qualityMedium': 'orta',
	'report.qualityLow': 'düşük',
	'report.exportHtml': 'HTML raporu',
	'report.tab.overview': 'Özet',
	'report.tab.findings': 'Bulgular',
	'report.tab.stats': 'İstatistik',
	'report.tab.refs': 'Atıflar',
	'report.tab.structure': 'Yapı & dil',
	'report.tab.access': 'Erişim',
	'report.tab.method': 'Yöntem',
	'access.title': 'Erişim ve şeffaflık',
	'access.lead':
		'Veri/kod beyanları, çıkar çatışması, destek ve etik bilgileri ile tanımlayıcı biçimleri. Kontroller yereldir; hiçbir bağlantı açılmaz.',
	'access.score': 'Erişim puanı',
	'access.checksTitle': 'Beyanlar ve tanımlayıcılar',
	'access.check.data': 'Veri erişimi',
	'access.check.code': 'Kod erişimi',
	'access.check.coi': 'Çıkar çatışması',
	'access.check.funding': 'Destek / fon',
	'access.check.ethics': 'Etik onay',
	'access.check.doi': 'DOI biçimleri',
	'access.check.url': 'URL biçimleri',
	'access.status.ok': 'mevcut',
	'access.status.missing': 'eksik',
	'access.status.invalid': 'geçersiz',
	'access.status.not_applicable': 'uygulanamaz',
	'access.status.na': 'uygulanamaz',
	'access.human': 'İnsan katılımcılı çalışma algılandı',
	'access.computational': 'Hesaplamalı çalışma algılandı',
	'access.findings': '{n} erişim bulgusu',
	'access.allGood': 'Beklenen beyanların tümü mevcut; tanımlayıcı biçimleri geçerli görünüyor.',
	'access.detected': 'Algılanan: {text}',
	'access.note': 'Ağ isteği yapılmaz; yalnızca yerel desen kontrolü.',
	'guideline.title': 'Raporlama kılavuzu',
	'guideline.detected': '{name} uygulanabilir — {total} kritik maddenin {present} tanesi bulundu.',
	'guideline.missing': 'eksik',
	'guideline.present': 'mevcut',
	'guideline.none':
		'Çalışma türü CONSORT/STROBE/PRISMA ile eşleşmedi; yalnızca genel yapı kontrolü uygulandı.',
	'guideline.name.consort': 'CONSORT',
	'guideline.name.strobe': 'STROBE',
	'guideline.name.prisma': 'PRISMA',
	'guideline.type.rct': 'Randomize kontrollü deneme',
	'guideline.type.observational': 'Gözlemsel çalışma',
	'guideline.type.systematic-review': 'Sistematik derleme / meta-analiz',
	'guideline.type.unknown': 'Belirsiz çalışma türü',
	'report.compare': 'Öncekiyle karşılaştır',
	'history.eyebrow': 'Rapor geçmişi',
	'history.title': 'Önceki raporu seç',
	'history.sub': 'Yalnızca bu tarayıcıda saklanır — son 20 rapor tutulur.',
	'history.empty': 'Henüz önceki rapor yok. Karşılaştırmak için revize makaleyi analiz edin.',
	'history.issues': '{n} bulgu',
	'diff.eyebrow': 'Sürüm karşılaştırması',
	'diff.title': 'Önceki ve güncel rapor',
	'diff.previous': 'Önceki',
	'diff.current': 'Güncel',
	'diff.categories': 'Kategori puanları',
	'diff.category': 'Kategori',
	'diff.change': 'Değişim',
	'diff.fixed': 'Düzeltilmiş ({n})',
	'diff.added': 'Yeni ({n})',
	'diff.ongoing': 'Devam eden ({n})',
	'diff.none': 'Yok',
	'report.download': 'Raporu indir (yazdır → PDF)',
	'report.pdf': 'PDF',
	'report.markdown': 'Markdown olarak panoya kopyala',
	'report.md': 'MD',
	'report.copied': 'Kopyalandı',
	'print.score': 'Skor',
	'print.categories': 'Kategori puanları',
	'print.category': 'Kategori',
	'print.weight': 'Ağırlık',
	'print.issues': 'Bulgular',
	'print.footer': 'RepCheck ile üretildi — repcheck.pages.dev',
	'export.mdTitle': 'RepCheck Raporu',
	'export.mdScore': 'Skor',
	'export.mdDate': 'Tarih',
	'export.mdIssues': 'Bulgular',
	'export.mdEmpty': 'Bulgu yok.',
	'revision.short': 'Mektup',
	'revision.title': 'Revizyon mektubu oluştur',
	'revision.eyebrow': 'Hakem yanıtı',
	'revision.sub': '{n} kararlı bulgu mektuba dahil edilecek.',
	'revision.letterTitle': 'Revizyon mektubu',
	'revision.copy': 'Mektubu kopyala',
	'revision.copied': 'Kopyalandı',
	'revision.regenerate': 'Yeniden oluştur',
	'revision.manualHint': 'Elle düzenlendi — üzerine yazmak için yeniden oluşturun.',
	'revision.empty': 'Mektuba eklenecek kararlı bulgu yok.',
	'revision.letter.greeting': 'Sayın Editör,',
	'revision.letter.intro':
		'Yapıcı geri bildiriminiz için teşekkür ederiz. Değerlendirme sürecinde belirtilen her noktayı dikkatle ele aldık. Aşağıda otomatik gönderim öncesi kontrol bulgularına verdiğimiz ayrıntılı yanıtlar yer almaktadır.',
	'revision.letter.closing':
		'Bu revizyonların makaleyi güçlendirdiğine inanıyoruz. Değerlendirmelerinizi bekleriz.',
	'revision.letter.signoff': 'Saygılarımızla,',
	'revision.letter.authors': '[Yazarlar]',
	'revision.letter.finding': 'Bulgu',
	'revision.letter.response': 'Yanıt',
	'revision.letter.status': 'Durum',
	'revision.letter.placeholder': 'Düzeltmenizi açıklayın…',
	'revision.letter.addressed': '{n} bulgu ele alındı',
	'revision.status.corrected': 'Düzeltildi',
	'revision.status.clarified': 'Netleştirildi',
	'revision.status.no_change': 'Değişiklik gerekmedi',
	'revision.status.acknowledged': 'Not alındı',
	'batch.modeAria': 'Analiz modu',
	'batch.single': 'Tekli analiz',
	'batch.batch': 'Toplu analiz',
	'batch.eyebrow': 'Toplu analiz',
	'batch.title': 'Birden fazla makaleyi analiz edin',
	'batch.lede': 'En fazla {n} dosya seçin. Her biri tarayıcınızda analiz edilir; hiçbir şey yüklenmez.',
	'batch.drop': 'Makaleleri buraya bırakın',
	'batch.or': 'veya',
	'batch.chooseFiles': 'Dosya seç',
	'batch.chooseFolder': 'Klasör seç',
	'batch.formats': 'PDF, DOCX, TeX, TXT — en fazla {n} dosya, her biri 40 MB',
	'batch.errorUnsupported': '{n} dosya atlandı: desteklenmeyen biçim.',
	'batch.errorSize': '{n} dosya atlandı: boş veya 40 MB’den büyük.',
	'batch.errorLimit': 'Aynı anda en fazla {n} dosya analiz edilebilir.',
	'batch.remove': 'Dosyayı kaldır',
	'batch.selected': '{n}/{max} seçildi',
	'batch.start': '{n} dosyayı analiz et',
	'batch.kb': '{n} KB',
	'batch.progressTitle': 'Toplu analiz sürüyor',
	'batch.progressSub': '{done}/{total} tamamlandı',
	'batch.cancelAll': 'Tümünü iptal et',
	'batch.cancel': 'İptal',
	'batch.status.queued': 'sırada',
	'batch.status.extracting': 'metin çıkarılıyor',
	'batch.status.analyzing': 'analiz ediliyor',
	'batch.status.done': 'tamamlandı',
	'batch.status.error': 'hata',
	'batch.status.cancelled': 'iptal edildi',
	'batch.summaryTitle': 'Toplu analiz özeti',
	'batch.summarySub': '{done}/{total} analiz edildi',
	'batch.csv': 'CSV',
	'batch.json': 'JSON',
	'batch.new': 'Yeni toplu analiz',
	'batch.previous': 'Önceki çalıştırmayla karşılaştırıldı: {id}',
	'batch.partial': '{failed} başarısız, {cancelled} iptal edildi.',
	'batch.sort': 'Skora göre sırala',
	'batch.sortDesc': 'Skor ↓',
	'batch.sortAsc': 'Skor ↑',
	'batch.filterGrade': 'Not',
	'batch.allGrades': 'Tüm notlar',
	'batch.file': 'Dosya',
	'batch.score': 'Skor',
	'batch.grade': 'Not',
	'batch.change': 'Değişim',
	'batch.open': 'Aç',
	'batch.empty': 'Bu filtreye uyan tamamlanmış analiz yok.',
	'journal.label': 'Hedef dergi',
	'journal.placeholder': 'Dergi ara…',
	'journal.none': 'Belirtilmedi',
	'journal.clear': 'Dergi seçimini temizle',
	'journal.selected': 'Seçili',
	'journal.noneHint': 'Dergi seçilmedi — yalnızca genel kontroller çalışır.',
	'journal.noResults': 'Eşleşen dergi yok.',
	'journal.rulesTitle': 'Dergi kuralları',
	'journal.compliance': '{passed}/{total} dergi kuralı geçti (RepCheck skoruna dahil değildir).',
	'journal.pass': 'uygun',
	'journal.fail': 'dikkat',
	'journal.check.wordLimit': 'Uzunluk {actual} kelime; dergi hedefi en fazla {max}.',
	'journal.check.requiredSection': '{sectionLabel} bölümü zorunlu.',
	'journal.check.requiredStatement': 'Zorunlu beyan: {statementLabel}.',
	'journal.check.referenceStyle': 'Kaynaklar {detected} görünüyor; hedef stil: {style}.',
	'journal.check.formatRule': '{ruleLabel} bekleniyor.',
	'journal.statement.data': 'veri erişimi',
	'journal.statement.coi': 'çıkar çatışması',
	'journal.statement.funding': 'fon/destek',
	'journal.statement.ethics': 'etik onay',
	'journal.statement.author_contributions': 'yazar katkıları',
	'journal.statement.registration': 'deneme kaydı',
	'journal.rule.structured_abstract': 'Yapılandırılmış öz',
	'journal.rule.imrad': 'IMRaD yapısı',
	'journal.rule.keywords': 'Anahtar kelimeler',
	'journal.ref.numbered': 'numaralı (Vancouver/IEEE)',
	'journal.ref.author-year': 'yazar–yıl (APA/Harvard)',
	'journal.ref.unknown': 'tanımlanamadı',
	'badge.short': 'Rozet',
	'badge.eyebrow': 'Doğrulama rozeti',
	'badge.title': '“Checked with RepCheck” rozeti al',
	'badge.sub': 'Rozeti makalenize, preprint’inize veya profilinize ekleyin.',
	'badge.id': 'Rozet kimliği',
	'badge.note': 'Kendi beyanınıza dayanır. Bağımsız doğrulama yakında.',
	'badge.light': 'Açık',
	'badge.dark': 'Koyu',
	'badge.small': 'Küçük',
	'badge.large': 'Büyük',
	'badge.downloadSvg': 'SVG indir',
	'badge.downloadPng': 'PNG indir',
	'badge.html': 'HTML kodu',
	'badge.markdown': 'Markdown',
	'badge.text': 'Düz metin',
	'badge.copy': 'Kopyala',
	'badge.copied': 'Kopyalandı',
	'badge.loading': 'Rozet üretiliyor…',
	'auth.eyebrow': 'Hesaplar',
	'auth.title': 'RepCheck’e giriş yapın',
	'auth.subtitle': "Ayrıntılı raporları açmak için LibEdge hesabınızla giriş yapın. Aktif RepCheck ataması gerekir.",
	'auth.orcid': 'ORCID ile devam et',
	'auth.google': 'Google ile devam et',
	'auth.libedge': 'LibEdge hesabınızla giriş yapın',
	'auth.or': 'veya sihirli bağlantı kullan',
	'auth.email': 'E-posta',
	'auth.code': '6 haneli kod',
	'auth.sent': 'Gelen kutunuzu kontrol edin — bağlantı ve kod 10 dakika geçerlidir.',
	'auth.verify': 'Kodu doğrula',
	'auth.sendLink': 'Sihirli bağlantı gönder',
	'auth.note': "Belgeniz ve metni cihazınızda kalır. Erişimi ve istatistikleri yönetmek için hesap bilgileri ve sayısal kullanım/sonuç verileri saklanır.",
	'auth.error.generic': 'Giriş başarısız. Lütfen tekrar deneyin.',
	'auth.error.send': 'E-posta gönderilemedi. Daha sonra tekrar deneyin.',
	'auth.error.code': 'Kod geçersiz veya süresi dolmuş.',
	'auth.signIn': 'Giriş yap',
	'auth.signOut': 'Çıkış yap',
	'auth.plan.free': 'Ücretsiz',
	'auth.plan.pro': 'Pro',
	'auth.limit.title': 'Aylık limit doldu',
	'auth.limit.body': 'Ücretsiz hesaplar ayda 5 makale analiz edebilir. Sınırsız analiz için Pro’ya geçin.',
	'auth.limit.upgrade': 'Pro’ya yükselt',
	'auth.limit.later': 'Şimdi değil',
	'auth.sync.note': 'Raporlarınızı cihazlar arasında tutmak için giriş yapın.',
	'journal.style.vancouver': 'Vancouver',
	'journal.style.ieee': 'IEEE',
	'journal.style.apa': 'APA',
	'journal.style.harvard': 'Harvard',
	'report.hideText': 'Metni gizle',
	'report.showText': 'Metni göster',
	'report.charsK': '{n} bin karakter',
	'report.tabsAria': 'Rapor bölümleri',

	// -------------------------------------------------------------- overview
	'overview.eyebrow': 'Genel değerlendirme',
	'overview.critical': '{n} kritik',
	'overview.warning': '{n} uyarı',
	'overview.info': '{n} bilgi',
	'overview.undecided':
		'{n} bulgu düşük güven nedeniyle karara dahil edilmedi; bunlar manuel doğrulama gerektirir.',
	'overview.verdict.clean':
		'<strong>Raporlama büyük ölçüde tutarlı.</strong> Kritik bir çelişki bulunmadı; yalnızca küçük iyileştirmeler önerilir.',
	'overview.verdict.critical':
		'<strong>{n} kritik bulgu</strong> istatistiksel raporlamanın veya kaynakçanın doğrulanmasını gerektiriyor. Bu bulgular ham veriyle teyit edilmeden yayına gönderilmemelidir.',
	'overview.verdict.good':
		'<strong>Genel olarak iyi.</strong> Birkaç raporlama boşluğu var; düzeltmeler hızlıca yapılabilir.',
	'overview.verdict.medium':
		'<strong>Orta düzeyde sorunlar.</strong> Birden fazla uyarı bir arada; raporlama standardı gözden geçirilmeli.',
	'overview.verdict.bad':
		'<strong>Ciddi raporlama sorunları.</strong> Genel skor {score} (not {grade}); bulguların tek tek ele alınması gerekir.',
	'overview.verdict.insufficient':
		'<strong>Kapsam yetersiz.</strong> Denetlenebilen kategori sayısı çok az olduğu için harf notu verilmedi. Bulguları manuel olarak inceleyin.',
	'overview.coverage': '{checked}/{total} denetim kategorisi kontrol edildi.',
	'overview.insufficientCoverage':
		'{checked}/{total} kategori kontrol edildi — kapsam %50’nin altında, not verilmedi.',
	'overview.qualityEyebrow': 'Metin çıkarımı güvenilirliği',
	'overview.qualityOk': 'Metin katmanı sağlıklı görünüyor; bulgular doğrudan metne dayanıyor.',
	'overview.categoriesTitle': 'Kategori skorları',
	'overview.categoriesSub': 'Skor, yalnızca yüksek/orta güvenli bulgulardan düşülerek hesaplanır.',
	'overview.priorityTitle': 'Öncelikli bulgular',
	'overview.prioritySub': 'Karara dahil edilen en önemli {n} bulgu.',
	'overview.priorityNone': 'Karara dahil edilen kritik veya uyarı düzeyinde bulgu yok.',
	'overview.coverageTitle': 'Kapsam',
	'overview.covStats': 'İstatistik ifadesi',
	'overview.covStatsNote': '{consistent} tutarlı · {inconsistent} çelişkili',
	'overview.covCitations': 'Metin içi atıf',
	'overview.covCitationsNote': '{matched} eşleşti · {missing} eksik',
	'overview.covReferences': 'Kaynakça girdisi',
	'overview.covReferencesNote': '{orphans} atıfsız',
	'overview.covFigures': 'Tablo / şekil',
	'overview.covFiguresNote': '{unreferenced} atıfsız',
	'overview.covLanguage': 'Dilsel işaret',
	'overview.covLanguageNote': '{causal} gerekçesiz nedensel',
	'overview.design': 'Çalışma deseni:',

	// -------------------------------------------------------------- findings
	'findings.all': 'Tümü',
	'findings.category': 'Kategori',
	'findings.searchLabel': 'Ara',
	'severity.label': 'Önem düzeyi',
	'findings.critical': 'Kritik',
	'findings.warning': 'Uyarı',
	'findings.info': 'Bilgi',
	'findings.categoryAll': 'Tüm kategoriler',
	'findings.search': 'Bulgu ara…',
	'findings.includeUndecided': 'Karara dahil edilmeyenleri de göster',
	'findings.showing': '{n} bulgu gösteriliyor',
	'findings.showingUndecided': ' · {n} tanesi karara dahil değil',
	'findings.empty': 'Bu filtreyle eşleşen bulgu yok.',
	'findings.none': 'Bulgu yok.',
	'findings.legend': 'Önem düzeyi açıklaması',
	'findings.legendText': '{label}: {hint}',

	// ------------------------------------------------------------- issue card
	'issue.notDecisive': 'karara dahil değil',
	'issue.suggestion': 'Öneri',
	'issue.showInText': 'Metinde göster',
	'issue.confidenceTitle': 'Güven: {level}',
	'issue.lowConfidence': 'düşük güven',

	// ----------------------------------------------------------------- stats
	'stats.total': 'istatistik ifadesi',
	'stats.consistent': 'tutarlı',
	'stats.inconsistent': 'çelişki / sapma',
	'stats.unverifiable': 'doğrulanamadı',
	'stats.empty':
		'Raporlanmış istatistik ifadesi bulunamadı. Metinde t(48) = 2.41, p = .019 gibi ifadeler aranır.',
	'stats.emptyMono': 't(48) = 2.41, p = .019',
	'stats.col.expression': 'İfade',
	'stats.col.value': 'Değer',
	'stats.col.df': 'df / n',
	'stats.col.reported': 'Raporlanan p',
	'stats.col.computed': 'Hesaplanan p',
	'stats.col.status': 'Durum',
	'stats.col.confidence': 'Güven',
	'stats.status.consistent': 'Tutarlı',
	'stats.status.flip': 'Çelişki',
	'stats.status.deviation': 'Sapma',
	'stats.status.unverifiable': 'Doğrulanamadı',
	'stats.status.uncomputable': 'Hesaplanamadı',
	'stats.delta': 'Fark:',
	'stats.boundNote': 'raporlanan bir sınır (p < .001)',
	'stats.showInText': 'Metinde göster',
	'stats.claimsTitle': 'Anlamlılık iddiaları',
	'stats.claimsHint': '"Anlamlı" / "significant" ifadelerinin aynı cümlede sayısal destek içerip içermediği.',
	'stats.supported': 'destekli',
	'stats.unsupported': 'desteksiz',
	'stats.negated': 'olumsuz',
	'stats.methodTitle': 'p değeri nasıl yeniden hesaplanıyor?',
	'stats.methodT': 't → Student t dağılımı, df = t(·) içindeki değer, iki yönlü.',
	'stats.methodF': 'F → merkezi F dağılımı, F(df1, df2), üst kuyruk.',
	'stats.methodChi': 'χ² → ki-kare dağılımı, χ²(df), üst kuyruk.',
	'stats.methodR': 'r → t = r√((n−2)/(1−r²)), df = n−2, iki yönlü.',
	'stats.methodZ': 'z → standart normal dağılım, iki yönlü.',
	'stats.tolerance':
		'Tolerans: log₁₀ ölçeğinde 0.3 birim (yaklaşık 2 kat) veya mutlak 0.005. Bunun ötesindeki farklar "sapma"; anlamlılık eşiğini geçen farklar "çelişki" olarak işaretlenir. Düşük güvenli ifadelerde çelişki bile karar olarak raporlanmaz.',
	'stats.notComputable': 'yeniden hesaplanamadı',
	'stats.twoTailed': 'iki yönlü',
	'stats.oneTailed': 'tek yönlü',
	'stats.pShort': 'p = {p}',

	// ------------------------------------------------------------ references
	'refs.citations': 'metin içi atıf',
	'refs.references': 'kaynakça girdisi',
	'refs.matched': 'eşleşen',
	'refs.missing': 'kaynakçada yok',
	'refs.orphans': 'atıf verilmeyen',
	'refs.noSectionTitle': 'Kaynakça bölümü tespit edilemedi.',
	'refs.noSectionBody':
		'"Kaynakça", "Kaynaklar" veya "References" başlığı bulunamadığı için atıf çapraz kontrolü yapılamadı.',
	'refs.noSectionCitations': 'Metinde {n} atıf var; bunlar doğrulanamadı.',
	'refs.tab.matches': 'Eşleşmeler',
	'refs.tab.missing': 'Eksik / uyuşmaz',
	'refs.tab.orphans': 'Atıfsız kaynaklar',
	'refs.col.citation': 'Metin içi atıf',
	'refs.col.status': 'Durum',
	'refs.col.match': 'Kaynakça eşleşmesi',
	'refs.status.matched': 'eşleşti',
	'refs.status.missing': 'kaynakçada yok',
	'refs.status.year': 'yıl uyuşmuyor',
	'refs.status.author': 'yazar uyuşmuyor',
	'refs.similar': 'Benzer girdiler:',
	'refs.emptyMatches': 'Metin içi atıf bulunamadı.',
	'refs.emptyMissing': 'Tüm metin içi atıflar kaynakçayla eşleşiyor.',
	'refs.emptyOrphans': 'Kaynakçadaki tüm girdilere metinde atıf verilmiş.',
	'refs.uncited': 'metinde atıf yok',
	'refs.showInText': 'Metinde göster',
	'refs.etal': 'vd.',
	'refs.viewAria': 'Atıf görünümü',
	'refs.yearArrow': 'yıl: {from} → {to}',
	'refs.methodTitle': 'Eşleştirme nasıl çalışıyor?',
	'refs.methodBody':
		'Metin içi atıflar (Yılmaz, 2019; (Smith & Jones, 2020); Öztürk vd., 2021) yazar soyadı + yıl anahtarına indirgenir. Kaynakça girdileri aynı şekilde ayrıştırılır. Soyadları Jaro-Winkler benzerliğiyle (eşik 0.86) karşılaştırılır; yıl birebir eşleşmezse "yıl uyuşmazlığı", yazar da eşleşmezse "yazar uyuşmazlığı" raporlanır. Eşleşmeyen atıflar kritik, kullanılmayan kaynaklar uyarı olarak listelenir.',

	// ------------------------------------------------------------- structure
	'struct.sectionsTitle': 'Bölüm haritası',
	'struct.sectionsSub': 'IMRaD yapısı aranır; başlıklar numaralı veya düz metin olabilir.',
	'struct.section.abstract': 'Öz',
	'struct.section.keywords': 'Anahtar kelimeler',
	'struct.section.introduction': 'Giriş',
	'struct.section.method': 'Yöntem',
	'struct.section.results': 'Bulgular',
	'struct.section.discussion': 'Tartışma',
	'struct.section.references': 'Kaynakça',
	'struct.section.appendix': 'Ekler',
	'struct.found': 'bulundu',
	'struct.notFound': 'bulunamadı',
	'struct.detail': 'Detay',
	'struct.gaps': 'Numaralandırma boşlukları:',
	'struct.figuresTitle': 'Tablo ve şekiller',
	'struct.figuresSub': 'Altyazı ↔ metin içi atıf çapraz kontrolü.',
	'struct.tables': 'Tablolar',
	'struct.figures': 'Şekiller',
	'struct.tableNo': 'Tablo {n}',
	'struct.figureNo': 'Şekil {n}',
	'struct.referenced': 'atıflı',
	'struct.unreferenced': 'atıfsız',
	'struct.noFigures': 'Tablo veya şekil altyazısı bulunamadı.',
	'struct.dangling': 'Metinde atıf var ama altyazı yok:',
	'struct.languageTitle': 'Dil ve iddia düzeyi',
	'struct.languageSub':
		'Nedensel dil, aşırı iddia ve test edilmemiş anlamlılık ifadeleri. Çalışma deseni:',
	'struct.designEvidence': 'kanıt:',
	'struct.emptyLang': 'Dikkat çeken dilsel kalıp bulunamadı.',
	'struct.lang.causal': 'nedensel',
	'struct.lang.causalJustified': 'nedensel (desen destekliyor)',
	'struct.lang.absolutist': 'aşırı iddia',
	'struct.lang.generalization': 'genelleme',
	'struct.lang.significance': 'desteksiz anlamlılık',

	// -------------------------------------------------------------- method
	'method.title': 'Nasıl çalışıyor?',
	'method.leadA': 'RepCheck, makaleyi',
	'method.leadStrong': 'yalnızca tarayıcınızda',
	'method.leadB':
		'işler. Dosya hiçbir sunucuya gönderilmez; sayfa yenilendiğinde bellekten silinir. Amaç, yayın öncesi bir editörün yakalayabileceği raporlama tutarsızlıklarını hızlıca görünür kılmaktır — kesin bir hakem kararı değil,',
	'method.leadEm': 'ön denetim',
	'method.pipe.extract': 'Çıkarım',
	'method.pipe.parse': 'Ayrıştırma',
	'method.pipe.cross': 'Çapraz kontrol',
	'method.pipe.confidence': 'Güven filtresi',
	'method.pipe.score': 'Skor + annotasyon',
	'method.pipelineAria': 'İşlem hattı',
	'method.confColLevel': 'Güven',
	'method.confColCond': 'Tipik koşul',
	'method.confColEffect': 'Skora etkisi',
	'method.extractTitle': '1 · Metin çıkarımı',
	'method.extractPdf': "PDF — pdf.js, ayrı bir Web Worker'da, sayfa sayfa. Her metin satırının bounding box'ı saklanır.",
	'method.extractDocx': 'DOCX — mammoth.js ham paragraf metni üretir; sayfa geometrisi yoktur.',
	'method.extractTex': 'LaTeX — kaynak doğrudan ayrıştırılır; bölüm, tablo/şekil ve kaynakça yapısı en güvenilir burada çıkar.',
	'method.operatorNote':
		"PDF'te operatör kaybı gerçek bir sorundur: p < .0001 çıkarımda p , .0001 olabilir. Motor bu kalıpları ayrıca işaretler ve",
	'method.operatorNoteStrong': 'karar için kullanmaz',
	'method.confTitle': '2 · Güven skoru',
	'method.confBody':
		'Her bulgu yüksek / orta / düşük güven taşır. Kural: yalnızca yüksek ve orta güvenli bulgular skoru ve "hata" kararını etkiler. Düşük güvenli bulgular listede kalır ama',
	'method.confBodyStrong': 'karara dahil edilmez',
	'method.confHigh': 'yüksek',
	'method.confHighCond': 'Açık istatistik + df/n + p, net operatör',
	'method.confHighEffect': 'tam ceza',
	'method.confMedium': 'orta',
	'method.confMediumCond': 'p eksik, df eksik, virgüllü ondalık',
	'method.confMediumEffect': '%65 ceza',
	'method.confLow': 'düşük',
	'method.confLowCond': 'Operatör kaybı, olanaksız değer, belirsizlik',
	'method.confLowEffect': 'ceza yok',
	'method.confNote':
		'Belgenin genel metin kalitesi düşükse (taranmış PDF, bozuk font) tüm bulguların güveni bir kademe düşürülür.',
	'method.statsTitle': '3 · İstatistik denetimi',
	'method.statsBody':
		'Metindeki t(48) = 2.41, p = .019 gibi ifadeler ayrıştırılır; serbestlik dereceleri ve örneklem büyüklüğü çıkarılır. p değeri jStat ile',
	'method.statsBodyStrong': 'yeniden hesaplanır',
	'method.statsBodyEnd': 've raporlanan değerle karşılaştırılır:',
	'method.statsFlip': 'Çelişki — biri p < .05, diğeri ≥ .05. Kritik bulgu.',
	'method.statsDev': 'Sapma — ikisi de aynı kararda ama fark büyük (log₁₀ > 0.3). Uyarı.',
	'method.statsNoP': 'p yok — istatistik var, p raporlanmamış. Uyarı.',
	'method.statsOp': 'Operatör kaybı — p , .05. Uyarı, karar dışı.',
	'method.statsT': 't → p = 2 · P(T > |t|), df',
	'method.statsF': 'F → p = P(F > f), df₁, df₂',
	'method.statsChi': 'χ² → p = P(χ² > x), df',
	'method.statsR': 'r → t = r√((n−2)/(1−r²))',
	'method.statsZ': 'z → p = 2 · P(Z > |z|)',
	'method.refTitle': '4 · Atıf ve kaynakça',
	'method.refBody':
		'Metin içi atıflar (parantez içi ve anlatı biçimi) ile kaynakça girdileri yazar soyadı + yıl anahtarıyla eşleştirilir. Soyadları Jaro-Winkler benzerliğiyle (eşik 0.86) karşılaştırılır; Türkçe karakterler sadeleştirilir. Kaynakçada olmayan atıf kritik, metinde atıf verilmeyen kaynak uyarıdır.',
	'method.langTitle': '5 · Yapı ve dil',
	'method.langBodyA':
		'IMRaD bölümleri, tablo/şekil altyazıları ve metin içi atıflar çapraz kontrol edilir. Nedensel dil (',
	'method.langBodyMonoA': 'neden oldu',
	'method.langBodyB': ', ',
	'method.langBodyMonoB': 'etkiledi',
	'method.langBodyC':
		') yöntem bölümünden çıkarılan çalışma desenine göre değerlendirilir: deneysel desende meşru, ilişkisel desende aşırı iddia. Desen belirlenemezse bulgu',
	'method.langBodyEm': 'bilgi',
	'method.langBodyD': 'düzeyinde kalır.',
	'method.scoreTitle': '6 · Ağırlıklı skor',
	'method.scoreBody':
		'Kategori skorları 100’den başlar; her kararlı bulgu, önem düzeyine (kritik 16, uyarı 7, bilgi 2.5) ve güven katsayısına göre düşülür. Aynı sorunun onlarca tekrarı skoru sıfıra sürüklemesin diye 6’dan fazla bulguda azalan etki uygulanır. Genel skor, ağırlıklı ortalamadır.',
	'method.scoreThresholds':
		'Eşikler: <strong>A</strong> ≥ 90, <strong>B</strong> ≥ 75, <strong>C</strong> ≥ 60, aksi halde <strong>D</strong>.',
	'method.limitsTitle': 'Sınırlar',
	'method.limit1': 'Bu araç <strong>hakem değildir</strong>. Bulgular metnin kendi içindeki tutarlılığa bakar; ham veriye veya etik kurul süreçlerine erişemez.',
	'method.limit2': "Taranmış (metin katmanı olmayan) PDF'ler analiz edilemez. OCR uygulanmış sürüm gerekir.",
	'method.limit3': 'Kaynakça biçimi çok düzensizse girdi ayrıştırması kısmi kalabilir; bu durumda atıf sonuçları "düşük güven" işareti taşır.',
	'method.limit4': 'İstatistik ifadeleri yalnızca standart APA benzeri kalıplarla tanınır; özel kısaltmalar gözden kaçabilir.',
	'method.limit5': 'Dil heuristikleri bağlam duyarlıdır; kesin yargı üretmez, işaret eder.',
	'method.logToggle': 'Analiz kaydı',
	'method.logFile': 'Dosya',
	'method.logFormat': 'Biçim',
	'method.logPages': 'Sayfa',
	'method.logChars': 'Karakter',
	'method.logQuality': 'Metin kalitesi',
	'method.logOperators': 'Operatör şüphesi',
	'method.logBlocks': 'Paragraf / blok',
	'method.logCreated': 'Oluşturulma',
	'method.logNotes': 'Notlar:',
	'method.appendixTitle': 'Bulgu dökümü',
	'method.appendixBody':
		'Raporun tam listesi; satır bazlı annotasyonla birlikte. Düşük güvenli bulgular ayrı işaretlidir.',
	'method.appendixUndecided': 'karara dahil edilmeyenler',

	// --------------------------------------------------------------- viewer
	'viewer.title': 'Belge görünümü',
	'viewer.lines': '{n} satır',
	'viewer.pages': '{n} sayfa',
	'viewer.annotated': '{n} işaretli konum',
	'viewer.highlight': 'Vurgu:',
	'viewer.countAria': '{n} bulgu',
	'viewer.flagAria': 'bu satırdaki bulgular',

	// ------------------------------------------------------------- test set
	'testSet.kicker': 'Doğrulama',
	'testSet.title': '25 makalelik test seti',
	'testSet.body':
		'Manifest şeması src/lib/data/testSet.ts dosyasında. Buradaki başlıklar kurgusal örnektir; kendi veri setinizle değiştirin. Her makale için beklenen asıl risk not edilmiştir — böylece motorun o riski yakalayıp yakalamadığı karşılaştırılabilir.',
	'testSet.inspected': 'denetlendi',
	'testSet.colArticle': 'Makale',
	'testSet.colId': 'Kod',
	'testSet.colField': 'Alan',
	'testSet.colFocus': 'Odak',
	'testSet.foot': "İşaretlemeler yalnızca bu tarayıcıda saklanır (localStorage); dışarı gönderilmez.",
	'testSet.markAria': '{id} denetlendi olarak işaretle',

	// ---------------------------------------------------------------- errors
	'err.unsupported':
		'Desteklenmeyen dosya türü. PDF, DOCX veya .tex dosyası yükleyin. (Eski .doc formatı desteklenmez.)',
	'err.docx': 'DOCX okunamadı: {message}. Dosya bozuk olabilir veya .doc (eski format) olabilir; Word’de .docx olarak kaydedin.',
	'err.docxEmpty': 'DOCX içinde metin bulunamadı; belge yalnızca görsel içeriyor olabilir.',
	'err.pdf':
		'PDF okunamadı. Dosya şifreli veya bozuk olabilir. Taranmış PDF ise metin katmanı yoktur.',
	'err.pdfWorker': 'PDF worker hatası: {message}',
	'err.pdfIncomplete': 'PDF çıkarımı tamamlanamadı.',
	'err.empty': 'Dosya boş görünüyor.',
	'err.tooShort': 'Analiz için en az 100 karakterlik metin gerekli.',
	'err.generic': 'Belge işlenirken bir sorun oluştu.',
	'export.title': 'RepCheck raporu',
	'export.summary': '{critical} kritik · {warning} uyarı · {undecided} karara dahil değil',
	'export.colCategory': 'Kategori',
	'export.colScore': 'Skor',
	'export.colWeight': 'Ağırlık',
	'export.colNote': 'Not',
	'export.findingsCount': '{n} bulgu',
	'export.confidence': 'güven: {level}',
	'export.footer':
		'Bu rapor otomatik bir ön denetimdir; kesin yargı yerine geçmez. Düşük güvenli bulgular manuel doğrulanmalıdır.',
	'export.csv.category': 'kategori',
	'export.csv.severity': 'önem',
	'export.csv.code': 'kod',
	'export.csv.title': 'başlık',
	'export.csv.detail': 'açıklama',
	'export.csv.confidence': 'güven',
	'export.csv.decisive': 'karara_dahil',
	'export.csv.page': 'sayfa',
	'export.csv.line': 'satır',
	'export.csv.quote': 'alıntı'
};

export const DICT: Record<'en' | 'tr', Dict> = { en, tr };
